// src/pages/coach/SwotPage.js
import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { isAthleticPriorityEntry } from "../../lib/swotPriorityRouter";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
    useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { v4 as uuidv4 } from 'uuid';

const EMPTY_SWOT_STATE = { strengths: [], weaknesses: [], opportunities: [], threats: [] };

const normalizeChecklistTask = (task) => {
    if (typeof task === "string") {
        const text = String(task || "");
        return {
            id: uuidv4(),
            text,
            is_completed: false,
            completed_at: null
        };
    }
    if (task && typeof task === "object") {
        const text = String(task.text || task.item || task.action || "");
        const isCompleted = Boolean(task.is_completed ?? task.completed);
        const completedAt = isCompleted
            ? String(task.completed_at || task.completedAt || "").trim() || null
            : null;
        return {
            id: String(task.id || uuidv4()),
            text,
            is_completed: isCompleted,
            completed_at: completedAt
        };
    }
    return { id: uuidv4(), text: "", is_completed: false, completed_at: null };
};

const normalizeMicroPointPlan = (action) => {
    // Legacy support: old SWOT action shape with text/completed.
    if (typeof action === "string") {
        const task = normalizeChecklistTask(action);
        return {
            microPointId: "",
            microPointTitle: "",
            tasks: task.text ? [task] : [],
            is_completed: false,
            completed_at: null
        };
    }
    if (action && typeof action === "object") {
        const microPointId = String(
            action.microPointId ||
            action.micro_point_id ||
            action.development_item_micro_point_id ||
            ""
        ).trim();
        const microPointTitle = String(
            action.microPointTitle ||
            action.micro_point_title ||
            action.microPoint ||
            ""
        ).trim();
        const legacyText = String(action.text || action.item || action.action || "").trim();
        const sourceTasks = Array.isArray(action.tasks)
            ? action.tasks
            : (legacyText ? [{ text: legacyText, completed: Boolean(action.completed) }] : []);
        const tasks = sourceTasks
            .map(normalizeChecklistTask);
        const isCompleted = tasks.length > 0
            ? tasks.every((task) => Boolean(task.is_completed))
            : Boolean(action.is_completed ?? action.completed);
        const completedAt = isCompleted
            ? String(action.completed_at || action.completedAt || "").trim() || new Date().toISOString()
            : null;
        return {
            microPointId,
            microPointTitle,
            tasks,
            is_completed: isCompleted,
            completed_at: completedAt
        };
    }
    return { microPointId: "", microPointTitle: "", tasks: [], is_completed: false, completed_at: null };
};

const normalizeActions = (actions) => {
    if (!Array.isArray(actions)) return [];
    return actions.map(normalizeMicroPointPlan);
};

const isActionFilled = (action) => {
    if (!action || typeof action !== "object") return false;
    if (String(action.microPointId || "").trim() !== "") return true;
    if (String(action.microPointTitle || "").trim() !== "") return true;
    const tasks = Array.isArray(action.tasks) ? action.tasks : [];
    return tasks.some((task) => String(task?.text || "").trim() !== "");
};

const isItemCompleted = (item) => {
    const actions = normalizeActions(item?.actions);
    const activeActions = actions.filter(isActionFilled);
    if (activeActions.length === 0) return Boolean(item?.completed);
    return activeActions.every((a) => Boolean(a.is_completed));
};

const normalizeSwotItem = (item) => {
    if (typeof item === "string") {
        return {
            id: uuidv4(),
            section: "General",
            item: item.trim(),
            actions: [],
            completed: false,
            priority: false,
            priorityRank: null
        };
    }
    const normalized = {
        ...(item || {}),
        id: item?.id || uuidv4(),
        section: String(item?.section || "General").trim() || "General",
        item: String(item?.item || "").trim(),
        actions: normalizeActions(item?.actions),
        priority: Boolean(item?.priority),
        priorityRank: Number.isFinite(item?.priorityRank) ? Number(item.priorityRank) : null
    };
    normalized.completed = isItemCompleted(normalized);
    return normalized;
};

const normalizeSwotCategory = (items) => {
    let source = items;
    if (typeof source === "string") {
        try {
            source = JSON.parse(source);
        } catch (err) {
            source = [];
        }
    }
    if (!Array.isArray(source)) return [];
    return source.map(normalizeSwotItem).filter((item) => item.item !== "");
};

const normalizeSwotState = (raw) => ({
    strengths: normalizeSwotCategory(raw?.strengths),
    weaknesses: normalizeSwotCategory(raw?.weaknesses),
    opportunities: normalizeSwotCategory(raw?.opportunities),
    threats: normalizeSwotCategory(raw?.threats)
});

const scopeKeyToPositionId = (scopeKey) => String(scopeKey || "").trim() || null;

const annotateSwotItemsWithPositionId = (swotState, positionId) => {
    const scoped = normalizeSwotState(swotState || EMPTY_SWOT_STATE);
    const next = {};
    Object.keys(scoped).forEach((category) => {
        next[category] = (scoped[category] || []).map((item) => ({
            ...item,
            position_id: positionId || null
        }));
    });
    return next;
};

const appendUniqueSwotItems = (baseItems, itemsToAppend, category) => {
    const next = Array.isArray(baseItems) ? [...baseItems] : [];
    (itemsToAppend || []).forEach((item) => {
        const key = String(item?.id || `${category}:${item?.item || ""}:${item?.section || ""}`).trim();
        if (!key) return;
        const exists = next.some((current) => {
            const currentKey = String(current?.id || `${category}:${current?.item || ""}:${current?.section || ""}`).trim();
            return currentKey === key;
        });
        if (!exists) next.push(item);
    });
    return next;
};

const splitSwotByAthleticBucket = (swotState) => {
    const normalized = normalizeSwotState(swotState || EMPTY_SWOT_STATE);
    const skill = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
    const athletic = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
    Object.keys(normalized).forEach((category) => {
        (normalized[category] || []).forEach((item) => {
            const bucket = isAthleticPriorityEntry({ ...item, category }) ? athletic : skill;
            bucket[category].push(item);
        });
    });
    return {
        skill: normalizeSwotState(skill),
        athletic: normalizeSwotState(athletic)
    };
};

const mergeSwotSkillAndAthletic = (skillState, athleticState) => {
    const skill = normalizeSwotState(skillState || EMPTY_SWOT_STATE);
    const athletic = normalizeSwotState(athleticState || EMPTY_SWOT_STATE);
    const merged = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
    Object.keys(merged).forEach((category) => {
        merged[category] = appendUniqueSwotItems(skill[category], athletic[category], category);
    });
    return normalizeSwotState(merged);
};

const mergeAthleticStates = (baseAthletic, incomingAthletic) => {
    const base = normalizeSwotState(baseAthletic || EMPTY_SWOT_STATE);
    const incoming = normalizeSwotState(incomingAthletic || EMPTY_SWOT_STATE);
    const merged = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
    Object.keys(merged).forEach((category) => {
        merged[category] = appendUniqueSwotItems(base[category], incoming[category], category);
    });
    return normalizeSwotState(merged);
};

const isSwotStateBlank = (state) => {
    const swotState = normalizeSwotState(state || EMPTY_SWOT_STATE);
    return Object.values(swotState).every((items) => Array.isArray(items) && items.length === 0);
};

const buildSwotPayloadFromPlayerType = async (playerTypeId) => {
    const { data: swotRows, error: swotRowsError } = await supabase
        .from("player_type_swot")
        .select("skill_category_id, skill_focus_id, swot_type, priority, notes")
        .eq("player_type_id", playerTypeId);
    if (swotRowsError) throw swotRowsError;
    if (!swotRows || swotRows.length === 0) return null;

    const sectionIds = Array.from(
        new Set(swotRows.map((row) => row.skill_category_id).filter(Boolean))
    );
    const itemIds = Array.from(
        new Set(swotRows.map((row) => row.skill_focus_id).filter(Boolean))
    );

    const [sectionsRes, itemsRes] = await Promise.all([
        sectionIds.length
            ? supabase
                .from("development_sections")
                .select("id, name")
                .in("id", sectionIds)
            : Promise.resolve({ data: [], error: null }),
        itemIds.length
            ? supabase
                .from("development_items")
                .select("id, name")
                .in("id", itemIds)
            : Promise.resolve({ data: [], error: null })
    ]);

    if (sectionsRes.error) throw sectionsRes.error;
    if (itemsRes.error) throw itemsRes.error;

    const sectionById = new Map((sectionsRes.data || []).map((row) => [row.id, row.name]));
    const itemById = new Map((itemsRes.data || []).map((row) => [row.id, row.name]));
    const buckets = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
    const swotTypeMap = {
        Strength: "strengths",
        Weakness: "weaknesses",
        Opportunity: "opportunities",
        Threat: "threats"
    };

    (swotRows || []).forEach((row) => {
        const bucketKey = swotTypeMap[row.swot_type];
        if (!bucketKey) return;
        const sectionName = sectionById.get(row.skill_category_id) || "General";
        const itemName = itemById.get(row.skill_focus_id) || "Skill Focus";
        buckets[bucketKey].push({
            id: uuidv4(),
            section: sectionName,
            sectionId: row.skill_category_id || null,
            developmentItemId: row.skill_focus_id || null,
            item: itemName,
            completed: false,
            priority: false,
            priorityRank: null,
            actions: [],
            notes: row.notes || ""
        });
    });

    return buckets;
};

const collectPriorityItems = (swotState) => (
    Object.entries(swotState).flatMap(([category, items]) =>
        (items || []).filter((item) => item.priority).map((item) => ({ ...item, category }))
    )
);

const assignPriorityRanks = (swotState, orderedPriorityIds) => {
    const rankById = new Map(orderedPriorityIds.map((id, idx) => [id, idx + 1]));
    return Object.fromEntries(
        Object.entries(swotState).map(([category, items]) => ([
            category,
            (items || []).map((item) => ({
                ...item,
                priorityRank: item.priority ? (rankById.get(item.id) || null) : null
            }))
        ]))
    );
};

const CATEGORY_SINGULAR_LABEL = {
    strengths: "Strength",
    weaknesses: "Weakness",
    opportunities: "Opportunity",
    threats: "Threat"
};

// --- COMPONENTS ---

function InfoTooltip({ text }) {
    return (
        <div className="group relative inline-block ml-2 cursor-help z-10">
            <span className="text-gray-400 bg-gray-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold hover:bg-gray-300">
                i
            </span>
            <div className="invisible group-hover:visible absolute z-20 w-64 p-3 mt-2 -left-20 text-sm leading-snug text-white bg-gray-800 rounded-md shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                {text}
                <div className="absolute -top-1 left-24 w-2 h-2 bg-gray-800 rotate-45"></div>
            </div>
        </div>
    );
}

function DetailModal({ item, sections, onClose, onSave }) {
    const [formData, setFormData] = useState({
        section: "",
        item: ""
    });

    useEffect(() => {
        if (item) {
            setFormData({
                section: item.section || "",
                item: item.item || ""
            });
        }
    }, [item]);

    if (!item) return null;

    const handleSaveClick = () => {
        const cleanData = {
            ...formData
        };
        onSave(item.id, item.category, cleanData);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all scale-100 flex flex-col max-h-[90vh]">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-800">Edit Item</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100">✕</button>
                </div>
                <div className="p-6 overflow-y-auto">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Category / Section</label>
                            <select
                                className="w-full text-sm border-gray-300 rounded-md shadow-sm border p-2 bg-gray-50 focus:bg-white focus:ring-blue-500 focus:border-blue-500"
                                value={formData.section}
                                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                            >
                                {sections && sections.every(s => s.name !== formData.section) && (
                                    <option value={formData.section}>{formData.section}</option>
                                )}
                                {sections && sections.map(s => (
                                    <option key={s.id} value={s.name}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Item Name</label>
                            <input
                                type="text"
                                className="w-full text-sm font-semibold border-gray-300 rounded-md shadow-sm border p-2 focus:ring-blue-500 focus:border-blue-500"
                                value={formData.item}
                                onChange={(e) => setFormData({ ...formData, item: e.target.value })}
                            />
                        </div>
                    </div>
                </div>
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition">Cancel</button>
                    <button onClick={handleSaveClick} className="px-6 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 shadow-md transition">Update Item</button>
                </div>
            </div>
        </div>
    );
}

function SortableItem({ data, onRemove, onClick, onTogglePriority, canEdit }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: data.id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    const itemCompleted = Boolean(data.completed);

    return (
        <li
            ref={setNodeRef}
            style={style}
            {...(canEdit ? attributes : {})}
            {...(canEdit ? listeners : {})}
            onClick={() => onClick(data)}
            className={`group relative bg-white border border-gray-200 rounded-lg shadow-sm mb-3 p-3 hover:shadow-md hover:border-blue-300 transition-all touch-none ${canEdit ? "cursor-pointer" : "cursor-default"}`}
        >
            <div className="flex justify-between items-start">
                <div>
                    <span className={`block font-bold text-sm ${itemCompleted ? "text-gray-400 line-through" : "text-gray-900"}`}>{data.item}</span>
                    <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mt-1">{data.section}</span>
                </div>
                <div className="flex items-center gap-1">
                    {canEdit ? (
                        <>
                            <button
                                type="button"
                                title={data.priority ? "Remove priority" : "Add to season priorities"}
                                className={`${data.priority ? "text-amber-500" : "text-gray-300"} hover:text-amber-500 rounded p-1 transition-all z-10`}
                                onClick={(e) => { e.stopPropagation(); onTogglePriority(data.category, data.id); }}
                                onPointerDown={(e) => e.stopPropagation()}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${data.priority ? "fill-current" : ""}`} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.174 3.617a1 1 0 00.95.69h3.804c.969 0 1.371 1.24.588 1.81l-3.078 2.236a1 1 0 00-.364 1.118l1.175 3.617c.3.922-.755 1.688-1.54 1.118l-3.078-2.236a1 1 0 00-1.176 0l-3.078 2.236c-.784.57-1.838-.196-1.539-1.118l1.174-3.617a1 1 0 00-.363-1.118L2.533 9.044c-.783-.57-.38-1.81.588-1.81h3.804a1 1 0 00.951-.69l1.173-3.617z" />
                                </svg>
                            </button>
                            <button
                                className="text-gray-300 hover:text-red-500 hover:bg-red-50 rounded p-1 transition-all z-10"
                                onClick={(e) => { e.stopPropagation(); onRemove(data.id); }}
                                onPointerDown={(e) => e.stopPropagation()}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </>
                    ) : null}
                </div>
            </div>
        </li>
    );
}

function ExpandablePriorityItem({
    data,
    priorityIndex,
    sections,
    itemsBySection,
    microPointsBySection,
    expanded,
    onToggleExpand,
    onTogglePriority,
    onMicroPointChange,
    onUpdateChecklistTaskText,
    onAddChecklistTask,
    onRemoveChecklistTask,
    canEdit
}) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: data.id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    const [openMicroIndex, setOpenMicroIndex] = useState(null);
    const normalizedActions = normalizeActions(data.actions);
    while (normalizedActions.length < 3) {
        normalizedActions.push({
            microPointId: "",
            microPointTitle: "",
            tasks: [],
            is_completed: false,
            completed_at: null
        });
    }
    const sectionIdFromName = sections.find(
        (section) => String(section.name || "").trim() === String(data.section || "").trim()
    )?.id;
    const sectionIdFromItemId = Object.entries(itemsBySection || {}).find(([, items]) =>
        (items || []).some((entry) => String(entry.id) === String(data.developmentItemId || ""))
    )?.[0];
    const sectionIdFromItemName = Object.entries(itemsBySection || {}).find(([, items]) =>
        (items || []).some((entry) => String(entry.name || "").trim() === String(data.item || "").trim())
    )?.[0];
    const sectionId = data.sectionId || sectionIdFromItemId || sectionIdFromItemName || sectionIdFromName || "";
    const microOptions = sectionId ? (microPointsBySection[sectionId] || []) : [];
    const enteredActions = normalizedActions.filter((action) => isActionFilled(action));
    const allDevelopmentItemsComplete = enteredActions.length > 0 && enteredActions.every((action) => Boolean(action.is_completed));

    useEffect(() => {
        if (!expanded) setOpenMicroIndex(null);
    }, [expanded, data.id]);

    return (
        <div ref={setNodeRef} style={style} className="bg-white border border-gray-200 rounded-xl shadow-sm touch-none">
            <button
                type="button"
                className="w-full p-4 text-left"
                onClick={() => onToggleExpand(data.id)}
                {...(canEdit ? attributes : {})}
                {...(canEdit ? listeners : {})}
            >
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                            {CATEGORY_SINGULAR_LABEL[data.category] || data.category}
                        </span>
                        <p className={`mt-1 text-base font-extrabold ${allDevelopmentItemsComplete ? "text-gray-400 line-through" : "text-gray-900"}`}>{data.item}</p>
                        <p className={`text-xs mt-1 ${allDevelopmentItemsComplete ? "text-gray-400 line-through" : "text-gray-500"}`}>{data.section}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center w-fit text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                            Priority #{priorityIndex}
                        </span>
                        {canEdit ? (
                            <button
                                type="button"
                                title="Remove from season priorities"
                                className="text-amber-500 hover:text-amber-600"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onTogglePriority(data.category, data.id);
                                }}
                                onPointerDown={(e) => e.stopPropagation()}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 fill-current" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.174 3.617a1 1 0 00.95.69h3.804c.969 0 1.371 1.24.588 1.81l-3.078 2.236a1 1 0 00-.364 1.118l1.175 3.617c.3.922-.755 1.688-1.54 1.118l-3.078-2.236a1 1 0 00-1.176 0l-3.078 2.236c-.784.57-1.838-.196-1.539-1.118l1.174-3.617a1 1 0 00-.363-1.118L2.533 9.044c-.783-.57-.38-1.81.588-1.81h3.804a1 1 0 00.951-.69l1.173-3.617z" />
                                </svg>
                            </button>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 fill-amber-500 text-amber-500" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.174 3.617a1 1 0 00.95.69h3.804c.969 0 1.371 1.24.588 1.81l-3.078 2.236a1 1 0 00-.364 1.118l1.175 3.617c.3.922-.755 1.688-1.54 1.118l-3.078-2.236a1 1 0 00-1.176 0l-3.078 2.236c-.784.57-1.838-.196-1.539-1.118l1.174-3.617a1 1 0 00-.363-1.118L2.533 9.044c-.783-.57-.38-1.81.588-1.81h3.804a1 1 0 00.951-.69l1.173-3.617z" />
                            </svg>
                        )}
                        <span className="text-gray-400 text-sm">{expanded ? "▾" : "▸"}</span>
                    </div>
                </div>
            </button>
            {expanded ? (
                <div className="px-4 pb-4 pt-1 border-t border-gray-100 space-y-2">
                    {[0, 1, 2].map((idx) => {
                        const action = normalizedActions[idx] || {
                            microPointId: "",
                            microPointTitle: "",
                            tasks: [],
                            is_completed: false,
                            completed_at: null
                        };
                        const tasks = Array.isArray(action.tasks) ? action.tasks : [];
                        const editableTasks = tasks.length > 0 ? tasks : [{ id: uuidv4(), text: "" }];
                        const isOpen = openMicroIndex === idx;
                        const filledStepsCount = editableTasks.filter(
                            (task) => String(task?.text || "").trim() !== ""
                        ).length;
                        return (
                            <div key={`${data.id}-micro-action-${idx}`} className="rounded-lg border border-gray-200 bg-gray-50/60">
                                <button
                                    type="button"
                                    className="w-full p-3 flex items-center justify-between"
                                    onClick={() => setOpenMicroIndex((prev) => (prev === idx ? null : idx))}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        {action?.microPointTitle ? (
                                            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider max-w-[230px] truncate">
                                                {action.microPointTitle}
                                            </span>
                                        ) : (
                                            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">{`Micro Point ${idx + 1}`}</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-bold text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
                                            {filledStepsCount}/{editableTasks.length}
                                        </span>
                                        <span className="text-gray-400 text-xs">{isOpen ? "▾" : "▸"}</span>
                                    </div>
                                </button>
                                {isOpen ? (
                                    <div className="px-3 pb-3 space-y-3">
                                        <select
                                            className="w-full text-xs rounded-md border border-gray-300 p-2 bg-white disabled:bg-gray-50 disabled:text-gray-400"
                                            value={action?.microPointId || ""}
                                            onChange={(e) => onMicroPointChange(data.category, data.id, idx, e.target.value, microOptions)}
                                            disabled={!canEdit}
                                        >
                                            <option value="">
                                                {sectionId
                                                    ? `Select micro point ${idx + 1}...`
                                                    : "No section mapped for this item"}
                                            </option>
                                            {microOptions.map((option) => (
                                                <option key={option.id} value={option.id}>{option.title}</option>
                                            ))}
                                        </select>
                                        {microOptions.length === 0 ? (
                                            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                                                No micro points found for this section.
                                            </p>
                                        ) : null}
                                        <div className="space-y-2">
                                            {editableTasks.map((task, taskIndex) => (
                                                <div key={`${data.id}-${idx}-step-${taskIndex}`} className="flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        value={task.text || ""}
                                                        placeholder={`Step ${taskIndex + 1}: How we'll accomplish this`}
                                                        className="w-full text-xs rounded-md border p-2 text-gray-700 border-gray-300 bg-white disabled:bg-gray-50 disabled:text-gray-400"
                                                        onChange={(e) => onUpdateChecklistTaskText(data.category, data.id, idx, taskIndex, e.target.value)}
                                                        disabled={!canEdit}
                                                    />
                                                    {canEdit ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => onRemoveChecklistTask(data.category, data.id, idx, taskIndex)}
                                                            className="h-8 px-2 shrink-0 rounded-md text-[11px] font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                                            title={`Delete Step ${taskIndex + 1}`}
                                                        >
                                                            x
                                                        </button>
                                                    ) : null}
                                                </div>
                                            ))}
                                            {canEdit ? (
                                                <button
                                                    type="button"
                                                    className="w-full rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
                                                    onClick={() => onAddChecklistTask(data.category, data.id, idx)}
                                                >
                                                    + Add Step
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}

// --- MAIN PAGE ---

export default function SwotPage() {
    const { athleteId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const draftSwotId = location.state?.draftSwotId;
    const isDraftSwot = Boolean(location.state?.isDraftSwot && draftSwotId);
    const effectiveAthleteId = !isDraftSwot ? String(athleteId || "").trim() : "";

    const [athlete, setAthlete] = useState(null);
    const [program, setProgram] = useState(null);
    const [sections, setSections] = useState([]);
    const [itemsBySection, setItemsBySection] = useState({});
    const [microPointsBySection, setMicroPointsBySection] = useState({});
    const [swot, setSwot] = useState({ strengths: [], weaknesses: [], opportunities: [], threats: [] });
    const [activeItem, setActiveItem] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [programName, setProgramName] = useState(""); // Add this state
    const [selectedSportId, setSelectedSportId] = useState("");
    const [playerTypeOptions, setPlayerTypeOptions] = useState([]);
    const [allSportPositions, setAllSportPositions] = useState([]);
    const [playerTypeByPrimaryPositionId, setPlayerTypeByPrimaryPositionId] = useState({});
    const [playerTypeBySecondaryPositionId, setPlayerTypeBySecondaryPositionId] = useState({});
    const [selectedPrimaryPositionId, setSelectedPrimaryPositionId] = useState("");
    const [selectedSecondaryPositionId, setSelectedSecondaryPositionId] = useState("");
    const [savingPositionConfig, setSavingPositionConfig] = useState(false);
    const [draftPrebuiltEmail, setDraftPrebuiltEmail] = useState("");
    const [positionOptions, setPositionOptions] = useState([]);
    const [selectedSwotPositionId, setSelectedSwotPositionId] = useState("");
    const [swotByScope, setSwotByScope] = useState({});
    const [draftStatus, setDraftStatus] = useState("draft");
    const [priorityError, setPriorityError] = useState("");
    const [expandedPriorityIds, setExpandedPriorityIds] = useState([]);
    const [priorityBucketTab, setPriorityBucketTab] = useState("skill");
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const [isSwotTipsModalOpen, setIsSwotTipsModalOpen] = useState(false);
    const [hasProgramSwot, setHasProgramSwot] = useState(false);
    const [isOtherProgramsOpen, setIsOtherProgramsOpen] = useState(false);
    const [otherProgramSwots, setOtherProgramSwots] = useState([]);
    const [hasUnsavedSwotChanges, setHasUnsavedSwotChanges] = useState(false);
    const isCoachRoute = location.pathname.startsWith("/coach/swot/");
    const forceReadOnly = Boolean(location.state?.readOnly);
    const canEdit = !forceReadOnly && (isCoachRoute || currentUserRole === "Coach" || currentUserRole === "Administrator");
    const resolvedDraftEmail = String(draftPrebuiltEmail || location.state?.prebuiltEmail || "").trim();
    const normalizeScopeId = (value) => String(value || "").trim();
    const getPlayerTypeName = (playerTypeId) => {
        const match = (playerTypeOptions || []).find((option) => String(option.id) === String(playerTypeId || ""));
        return match?.name || "";
    };
    const buildPlayerTypeMaps = (rows) => {
        const primaryMap = {};
        const secondaryMap = {};
        const timeByScope = {};
        (rows || []).forEach((row) => {
            const primaryKey = String(row.position_id || "").trim();
            const secondaryKey = String(row.secondary_position_id || "").trim();
            if (!row?.player_type) return;
            const updatedAt = new Date(row.updated_at || 0).getTime();
            if (primaryKey) {
                const existing = timeByScope[`p:${primaryKey}`] || 0;
                if (updatedAt >= existing) {
                    primaryMap[primaryKey] = String(row.player_type);
                    timeByScope[`p:${primaryKey}`] = updatedAt;
                }
            }
            if (secondaryKey) {
                const existing = timeByScope[`s:${secondaryKey}`] || 0;
                if (updatedAt >= existing) {
                    secondaryMap[secondaryKey] = String(row.player_type);
                    timeByScope[`s:${secondaryKey}`] = updatedAt;
                }
            }
        });
        return { primaryMap, secondaryMap };
    };
    const isSecondaryScope = (scopeKey) => {
        const scopeId = normalizeScopeId(scopeKey);
        if (!scopeId) return false;
        const secondaryId = normalizeScopeId(selectedSecondaryPositionId);
        if (secondaryId && scopeId === secondaryId) return true;
        const matchedOption = (positionOptions || []).find((opt) => String(opt.id) === scopeId);
        return Boolean(matchedOption && String(matchedOption.label || "").toLowerCase().includes("secondary"));
    };
    const getScopeColumns = (scopeKey) => {
        const scopeId = normalizeScopeId(scopeKey);
        if (!scopeId) return { position_id: null, secondary_position_id: null };
        if (isSecondaryScope(scopeId)) {
            return { position_id: null, secondary_position_id: scopeId };
        }
        return { position_id: scopeId, secondary_position_id: null };
    };
    const syncSwotDraftToScopes = (nextSwot) => {
        const scopeKey = String(selectedSwotPositionId || "").trim();
        if (!scopeKey) return;
        setSwotByScope((prev) => {
            const nextByScope = { ...prev };
            const currentScopeKeys = Object.keys(nextByScope);
            const targetScopeKeys = currentScopeKeys.length > 0 ? currentScopeKeys : [scopeKey];
            const { skill: nextSkill, athletic: nextAthletic } = splitSwotByAthleticBucket(nextSwot);

            targetScopeKeys.forEach((key) => {
                const existing = normalizeSwotState(nextByScope[key] || EMPTY_SWOT_STATE);
                const { skill: existingSkill } = splitSwotByAthleticBucket(existing);
                const skillForScope = String(key) === scopeKey ? nextSkill : existingSkill;
                nextByScope[key] = mergeSwotSkillAndAthletic(skillForScope, nextAthletic);
            });

            if (!nextByScope[scopeKey]) {
                nextByScope[scopeKey] = mergeSwotSkillAndAthletic(nextSkill, nextAthletic);
            }
            return nextByScope;
        });
    };
    const applySwotUpdate = (updater) => {
        setSwot((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            syncSwotDraftToScopes(next);
            return next;
        });
        setHasUnsavedSwotChanges(true);
    };

    useEffect(() => {
        async function fetchCurrentUserRole() {
            const { data: authData } = await supabase.auth.getUser();
            const authUserId = authData?.user?.id;
            if (!authUserId) return;
            const { data: roleData } = await supabase
                .from("users")
                .select("role")
                .eq("id", authUserId)
                .maybeSingle();
            setCurrentUserRole(roleData?.role || null);
        }
        fetchCurrentUserRole();
    }, []);

    const fetchRosterRowForProgram = async (targetProgramId) => {
        if (!effectiveAthleteId && resolvedDraftEmail && targetProgramId) {
            const { data: programTeams, error: teamError } = await supabase
                .from("teams")
                .select(`
                    id,
                    name,
                    program_id,
                    programs (id, name, sport)
                `)
                .eq("program_id", targetProgramId)
                .limit(1)
                .maybeSingle();
            if (teamError) return { data: null, error: teamError };
            if (!programTeams) return { data: null, error: { message: "No team found for draft athlete." } };

            return {
                data: {
                    user_id: null,
                    users: { full_name: "Draft Athlete" },
                    team: programTeams
                },
                error: null
            };
        }

        if (!targetProgramId) {
            return supabase
                .from("rostered_users")
                .select(`
                    user_id,
                    users (full_name),
                    team:teams!inner (
                        name,
                        program_id,
                        programs (id, name, sport)
                    )
                `)
                .eq("user_id", effectiveAthleteId)
                .limit(1)
                .single();
        }

        const { data: programTeams, error: teamError } = await supabase
            .from("teams")
            .select("id")
            .eq("program_id", targetProgramId);
        if (teamError) return { data: null, error: teamError };
        const teamIds = (programTeams || []).map((team) => team.id);
        if (teamIds.length === 0) return { data: null, error: { message: "No teams for program." } };

        return supabase
            .from("rostered_users")
            .select(`
                user_id,
                users (full_name),
                team:teams!inner (
                    name,
                    program_id,
                    programs (id, name, sport)
                )
            `)
            .eq("user_id", effectiveAthleteId)
            .in("team_id", teamIds)
            .limit(1)
            .single();
    };

    const fetchProgramById = async (programId) => {
        if (!programId) return { data: null, error: null };
        return supabase
            .from("programs")
            .select("id, name, sport")
            .eq("id", programId)
            .maybeSingle();
    };

    const fetchMicroPointsForSections = async (sectionRows) => {
        const sectionIds = Array.isArray(sectionRows)
            ? sectionRows.map((row) => row?.id).filter(Boolean)
            : [];
        if (sectionIds.length === 0) return { grouped: {} };

        const { data: rows, error } = await supabase
            .from("development_item_micro_points")
            .select("id, title, development_section_id")
            .in("development_section_id", sectionIds)
            .order("title");

        if (error) return { grouped: {} };

        const grouped = {};
        (rows || []).forEach((point) => {
            const sectionId = String(point.development_section_id || "").trim();
            if (!sectionId) return;
            const entry = {
                id: String(point.id || "").trim(),
                title: String(point.title || "").trim()
            };
            if (!entry.id) return;
            if (!grouped[sectionId]) grouped[sectionId] = [];
            grouped[sectionId].push(entry);
        });
        return { grouped };
    };

    const buildLaneOptionsFromSelections = (positions, primaryId, secondaryId) => {
        const byId = new Map((positions || []).map((row) => [String(row.id), row]));
        const orderedIds = [
            String(primaryId || "").trim(),
            String(secondaryId || "").trim()
        ].filter(Boolean);
        return orderedIds
            .map((id, idx) => {
                const row = byId.get(id);
                if (!row) return null;
                return {
                    id: String(row.id),
                    name: row.name,
                    label: idx === 0 ? `${row.name} (Primary)` : `${row.name} (Secondary)`
                };
            })
            .filter(Boolean);
    };

    const fetchPositionOptions = async (sportId, options = {}) => {
        if (!sportId) return;
        const targetProgramId = options?.targetProgramId || location.state?.programId || program?.id || null;
        const draftEmail = String(options?.draftEmail || resolvedDraftEmail || "").trim();
        setSelectedSportId(String(sportId));
        try {
            const { data: playerTypeRows, error: playerTypeError } = await supabase
                .from("player_types")
                .select("id, name")
                .eq("sport_id", sportId)
                .order("name", { ascending: true });
            if (playerTypeError) throw playerTypeError;
            setPlayerTypeOptions(playerTypeRows || []);
        } catch (err) {
            console.error("Error loading player types:", err);
            setPlayerTypeOptions([]);
        }
        let userSportInfo = null;
        let swotPositionIds = [];
        let draftPositionIds = [];
        if (effectiveAthleteId) {
            if (targetProgramId) {
                const { data: swotRows, error: swotRowsError } = await supabase
                    .from("athlete_swot")
                    .select("position_id, secondary_position_id, updated_at")
                    .eq("athlete_id", effectiveAthleteId)
                    .eq("program_id", targetProgramId)
                    .order("updated_at", { ascending: false });
                if (!swotRowsError && Array.isArray(swotRows)) {
                    const primaryRow = swotRows.find((row) => row?.position_id);
                    const secondaryRow = swotRows.find((row) => row?.secondary_position_id);
                    swotPositionIds = [
                        primaryRow?.position_id,
                        secondaryRow?.secondary_position_id
                    ]
                        .map((value) => String(value || "").trim())
                        .filter(Boolean);
                }
            }
            const { data, error: infoError } = await supabase
                .from("user_sport_info")
                .select("position, secondary_position")
                .eq("user_id", effectiveAthleteId)
                .eq("sport", sportId)
                .maybeSingle();
            if (infoError) return;
            userSportInfo = data;
        } else if (draftEmail && targetProgramId) {
            const { data: draftRows, error: draftRowsError } = await supabase
                .from("athlete_swot")
                .select("position_id, secondary_position_id")
                .is("athlete_id", null)
                .eq("program_id", targetProgramId)
                .ilike("prebuilt_email", draftEmail)
                .order("updated_at", { ascending: true });
            if (draftRowsError) return;
            draftPositionIds = Array.from(new Set(
                (draftRows || [])
                    .flatMap((row) => [row.position_id, row.secondary_position_id])
                    .map((value) => String(value || "").trim())
                    .filter(Boolean)
            ));
        }
        const fetchAllPositions = async () => {
            const pageSize = 200;
            let from = 0;
            let all = [];
            while (true) {
                const { data, error } = await supabase
                    .from("positions")
                    .select("id, name")
                    .eq("sport_id", sportId)
                    .order("name", { ascending: true })
                    .range(from, from + pageSize - 1);
                if (error) throw error;
                const rows = data || [];
                all = all.concat(rows);
                if (rows.length < pageSize) break;
                from += pageSize;
            }
            return all;
        };

        let allPositions = [];
        try {
            allPositions = await fetchAllPositions();
        } catch (err) {
            console.error("Error loading positions:", err);
            allPositions = [];
        }
        const fallbackSecondary = String(selectedSecondaryPositionId || "").trim();
        let fallbackPrimary = String(selectedPrimaryPositionId || allPositions[0]?.id || "").trim();
        let primaryId = String(swotPositionIds[0] || userSportInfo?.position || draftPositionIds[0] || fallbackPrimary).trim();
        const secondaryRaw = String(swotPositionIds[1] || userSportInfo?.secondary_position || draftPositionIds[1] || fallbackSecondary).trim();
        let secondaryId = secondaryRaw && secondaryRaw !== primaryId ? secondaryRaw : "";

        const missingPositionIds = [primaryId, secondaryId]
            .filter(Boolean)
            .filter((id) => !allPositions.some((row) => String(row.id) === String(id)));

        if (missingPositionIds.length > 0) {
            const { data: missingPositions } = await supabase
                .from("positions")
                .select("id, name")
                .in("id", missingPositionIds);

            if (missingPositions?.length) {
                const merged = new Map(
                    [...allPositions, ...missingPositions].map((row) => [String(row.id), row])
                );
                allPositions = Array.from(merged.values()).sort((a, b) =>
                    String(a.name || "").localeCompare(String(b.name || ""))
                );
            }
        }

        fallbackPrimary = String(selectedPrimaryPositionId || allPositions[0]?.id || "").trim();
        primaryId = String(swotPositionIds[0] || userSportInfo?.position || draftPositionIds[0] || fallbackPrimary).trim();
        secondaryId = secondaryRaw && secondaryRaw !== primaryId ? secondaryRaw : "";

        setAllSportPositions(allPositions);
        setSelectedPrimaryPositionId(primaryId);
        setSelectedSecondaryPositionId(secondaryId);

        const ordered = buildLaneOptionsFromSelections(allPositions, primaryId, secondaryId);
        setPositionOptions(ordered);
        setSelectedSwotPositionId((prev) => {
            const fullList = ordered.map((opt) => opt.id);
            const valid = fullList.includes(prev);
            return valid ? prev : (ordered[0]?.id || "");
        });
        return ordered;
    };

    const seedDraftSwotFromPlayerType = async ({ targetProgramId, playerTypeId, draftEmail, draftName }) => {
        const normalizedProgramId = String(targetProgramId || "").trim();
        const normalizedPlayerTypeId = String(playerTypeId || "").trim();
        const normalizedEmail = String(draftEmail || "").trim();
        if (!normalizedProgramId || !normalizedPlayerTypeId || !normalizedEmail) return false;

        const swotPayload = await buildSwotPayloadFromPlayerType(normalizedPlayerTypeId);
        if (!swotPayload) return false;

        const { data: existingSharedRow, error: existingSharedError } = await supabase
            .from("athlete_swot")
            .select("id, status")
            .is("athlete_id", null)
            .eq("program_id", normalizedProgramId)
            .ilike("prebuilt_email", normalizedEmail)
            .is("position_id", null)
            .is("secondary_position_id", null)
            .maybeSingle();
        if (existingSharedError) throw existingSharedError;

        const payload = {
            strengths: swotPayload.strengths,
            weaknesses: swotPayload.weaknesses,
            opportunities: swotPayload.opportunities,
            threats: swotPayload.threats,
            status: existingSharedRow?.status || "draft",
            prebuilt_name: String(draftName || "").trim() || null,
            prebuilt_email: normalizedEmail,
            player_type: normalizedPlayerTypeId,
            updated_at: new Date().toISOString()
        };

        if (existingSharedRow?.id) {
            const { error: updateError } = await supabase
                .from("athlete_swot")
                .update(payload)
                .eq("id", existingSharedRow.id)
                .is("athlete_id", null);
            if (updateError) throw updateError;
        } else {
            const { error: insertError } = await supabase
                .from("athlete_swot")
                .insert([{
                    athlete_id: null,
                    program_id: normalizedProgramId,
                    position_id: null,
                    secondary_position_id: null,
                    ...payload
                }]);
            if (insertError) throw insertError;
        }

        return true;
    };

    const loadSwotScopesForProgram = async (
        targetProgramId,
        selectedScopeKey = selectedSwotPositionId,
        scopeOptions = positionOptions,
        targetSportName = ""
    ) => {
        if (!athleteId || !targetProgramId) return;
        let { data: swotRows, error: swotError } = await supabase
            .from("athlete_swot")
            .select("position_id, secondary_position_id, player_type, strengths, weaknesses, opportunities, threats, updated_at")
            .eq("athlete_id", athleteId)
            .eq("program_id", targetProgramId)
            .order("updated_at", { ascending: false });
        if (swotError) throw swotError;
        setHasProgramSwot(Array.isArray(swotRows) && swotRows.length > 0);
        const isEmptyList = (value) => !Array.isArray(value) || value.length === 0;
        const isBlankRow = (row) => (
            isEmptyList(row?.strengths) &&
            isEmptyList(row?.weaknesses) &&
            isEmptyList(row?.opportunities) &&
            isEmptyList(row?.threats)
        );
        const hasNoRows = !swotRows || swotRows.length === 0;
        const hasOnlyBlankRows = !hasNoRows && swotRows.every(isBlankRow);
        if (hasNoRows || hasOnlyBlankRows) {
            const resolvedSportName = String(targetSportName || program?.sport || "").trim();
            if (resolvedSportName) {
                const { data: sportRow, error: sportError } = await supabase
                    .from("development_sports")
                    .select("id")
                    .eq("name", resolvedSportName)
                    .maybeSingle();
                if (sportError) throw sportError;
                if (sportRow?.id) {
                    let playerTypeId = "";
                    if (!hasNoRows) {
                        const seededRow = (swotRows || []).find((row) => row?.player_type);
                        playerTypeId = String(seededRow?.player_type || "").trim();
                    }
                    if (!playerTypeId) {
                        const { data: infoRow, error: infoError } = await supabase
                            .from("user_sport_info")
                            .select("player_type")
                            .eq("user_id", athleteId)
                            .eq("sport", sportRow.id)
                            .maybeSingle();
                        if (infoError) throw infoError;
                        playerTypeId = String(infoRow?.player_type || "").trim();
                    }
                    if (playerTypeId) {
                        const swotPayload = await buildSwotPayloadFromPlayerType(playerTypeId);
                        if (swotPayload) {
                            if (hasNoRows) {
                                const { error: insertError } = await supabase
                                    .from("athlete_swot")
                                    .insert([{
                                        program_id: targetProgramId,
                                        athlete_id: athleteId,
                                        status: "draft",
                                        strengths: swotPayload.strengths,
                                        weaknesses: swotPayload.weaknesses,
                                        opportunities: swotPayload.opportunities,
                                        threats: swotPayload.threats,
                                        player_type: playerTypeId
                                    }]);
                                if (insertError) throw insertError;
                            } else {
                                const { error: updateError } = await supabase
                                    .from("athlete_swot")
                                    .update({
                                        strengths: swotPayload.strengths,
                                        weaknesses: swotPayload.weaknesses,
                                        opportunities: swotPayload.opportunities,
                                        threats: swotPayload.threats,
                                        player_type: playerTypeId,
                                        updated_at: new Date().toISOString()
                                    })
                                    .eq("athlete_id", athleteId)
                                    .eq("program_id", targetProgramId);
                                if (updateError) throw updateError;
                            }
                            const refresh = await supabase
                                .from("athlete_swot")
                                .select("position_id, secondary_position_id, player_type, strengths, weaknesses, opportunities, threats, updated_at")
                                .eq("athlete_id", athleteId)
                                .eq("program_id", targetProgramId)
                                .order("updated_at", { ascending: false });
                            if (refresh.error) throw refresh.error;
                            swotRows = refresh.data || [];
                        }
                    }
                }
            }
        }

        if (Array.isArray(swotRows) && swotRows.length > 0) {
            const scoped = {};
            swotRows.forEach((row) => {
                const scopeKey = String(row.position_id || row.secondary_position_id || "").trim();
                if (!scopeKey || scoped[scopeKey]) return;
                scoped[scopeKey] = normalizeSwotState({
                    strengths: row.strengths || [],
                    weaknesses: row.weaknesses || [],
                    opportunities: row.opportunities || [],
                    threats: row.threats || []
                });
            });
            if (Object.keys(scoped).length > 0) {
                setSwotByScope(scoped);
                const nextScope = String(selectedScopeKey || "").trim() || Object.keys(scoped)[0] || "";
                if (nextScope) {
                    setSelectedSwotPositionId(nextScope);
                    setSwot(scoped[nextScope] || normalizeSwotState(EMPTY_SWOT_STATE));
                }
            }
            const { primaryMap, secondaryMap } = buildPlayerTypeMaps(swotRows);
            setPlayerTypeByPrimaryPositionId(primaryMap);
            setPlayerTypeBySecondaryPositionId(secondaryMap);
        }

        const baseSkillByScope = {};
        (scopeOptions || []).forEach((opt) => {
            baseSkillByScope[String(opt.id)] = normalizeSwotState(EMPTY_SWOT_STATE);
        });

        const sharedRow = (swotRows || []).find((row) => !row?.position_id && !row?.secondary_position_id) || null;
        const sharedState = sharedRow
            ? normalizeSwotState({
                strengths: sharedRow.strengths || [],
                weaknesses: sharedRow.weaknesses || [],
                opportunities: sharedRow.opportunities || [],
                threats: sharedRow.threats || []
            })
            : normalizeSwotState(EMPTY_SWOT_STATE);
        let sharedAthleticState = splitSwotByAthleticBucket(sharedState).athletic;

        const positionRows = (swotRows || []).filter((row) => Boolean(row?.position_id || row?.secondary_position_id));
        positionRows.forEach((row) => {
            const scopeKey = String(row.position_id || row.secondary_position_id || "").trim();
            if (!scopeKey) return;
            const laneState = normalizeSwotState({
                strengths: row.strengths || [],
                weaknesses: row.weaknesses || [],
                opportunities: row.opportunities || [],
                threats: row.threats || []
            });
            const { skill, athletic } = splitSwotByAthleticBucket(laneState);
            baseSkillByScope[scopeKey] = skill;
            if (!sharedRow) {
                sharedAthleticState = mergeAthleticStates(sharedAthleticState, athletic);
            }
        });

        const mergedByScope = {};
        const hasPositionRows = positionRows.length > 0;
        Object.keys(baseSkillByScope).forEach((scopeKey) => {
            if (hasPositionRows) {
                mergedByScope[scopeKey] = mergeSwotSkillAndAthletic(baseSkillByScope[scopeKey], sharedAthleticState);
            } else {
                mergedByScope[scopeKey] = normalizeSwotState(sharedState);
            }
        });

        setSwotByScope(mergedByScope);
        const laneScopes = (scopeOptions || []).map((opt) => String(opt.id)).filter(Boolean);
        const currentScope = selectedScopeKey || laneScopes[0] || "";
        const laneState = mergedByScope[currentScope] || normalizeSwotState(EMPTY_SWOT_STATE);
        setSwot(laneState);
    };

    const handleOpenOtherPrograms = async () => {
        if (!athleteId || !program?.id) return;
        setIsOtherProgramsOpen(true);
        try {
            const { data, error } = await supabase
                .from("athlete_swot")
                .select("id, program_id, status, updated_at, programs:program_id (name, sport)")
                .eq("athlete_id", athleteId)
                .neq("program_id", program.id)
                .order("updated_at", { ascending: false });
            if (error) throw error;
            setOtherProgramSwots(data || []);
        } catch (err) {
            console.error("Unable to load other program SWOTs:", err);
            setOtherProgramSwots([]);
        }
    };

    const handleViewOtherProgram = (row) => {
        if (!row?.program_id) return;
        const basePath = isCoachRoute ? "/coach/swot" : "/admin/swot";
        navigate(`${basePath}/${athleteId}`, {
            state: {
                programId: row.program_id,
                programName: row.programs?.name,
                sport: row.programs?.sport,
                readOnly: true
            }
        });
        setIsOtherProgramsOpen(false);
    };

    useEffect(() => {
        if (!athleteId && !isDraftSwot) return;
        async function fetchData() {
            try {
                if (isDraftSwot) {
                    const targetProgramId = location.state?.programId;
                    const targetSport = location.state?.sport;
                    const targetProgramName = location.state?.programName;
                    const targetDraftEmail = String(location.state?.prebuiltEmail || "").trim();

                    if (!targetProgramId || !targetSport || !draftSwotId) {
                        throw new Error("Missing draft SWOT context.");
                    }

                    setAthlete({
                        full_name: location.state?.prebuiltName || "Draft Athlete"
                    });
                    setProgram({ id: targetProgramId, sport: targetSport });
                    setProgramName(targetProgramName || "Draft Program");

                    const { data: sportData } = await supabase
                        .from("development_sports")
                        .select("id")
                        .eq("name", targetSport)
                        .single();
                    let draftScopeOptions = [];
                    if (sportData) {
                        const orderedOptions = await fetchPositionOptions(sportData.id, {
                            targetProgramId,
                            draftEmail: targetDraftEmail
                        });
                        draftScopeOptions = orderedOptions || [];
                        const { data: sectionsData } = await supabase
                            .from("development_sections")
                            .select("id, name")
                            .eq("sport_id", sportData.id)
                            .order("name");
                        setSections(sectionsData || []);
                        if (sectionsData?.length) {
                            const { data: itemsData } = await supabase
                                .from("development_items")
                                .select("id, name, section_id")
                                .in("section_id", sectionsData.map(s => s.id))
                                .order("name");
                            const grouped = {};
                            itemsData?.forEach(i => {
                                if (!grouped[i.section_id]) grouped[i.section_id] = [];
                                grouped[i.section_id].push(i);
                            });
                            setItemsBySection(grouped);
                            const microData = await fetchMicroPointsForSections(sectionsData || []);
                            setMicroPointsBySection(microData.grouped);
                        }
                    }

                    const { data: draftSwotData, error: draftSwotError } = await supabase
                        .from("athlete_swot")
                        .select("program_id, prebuilt_name, prebuilt_email, position_id, secondary_position_id, player_type, strengths, weaknesses, opportunities, threats, status")
                        .eq("id", draftSwotId)
                        .is("athlete_id", null)
                        .maybeSingle();

                    if (draftSwotError) throw draftSwotError;
                    if (draftSwotData) {
                        const draftEmail = String(draftSwotData.prebuilt_email || targetDraftEmail || "").trim();
                        const draftName = draftSwotData.prebuilt_name || location.state?.prebuiltName || "Draft Athlete";
                        setAthlete({
                            full_name: draftName
                        });
                        setDraftPrebuiltEmail(draftEmail);
                        let primaryScopeId = String(draftSwotData.position_id || selectedPrimaryPositionId || "").trim();
                        let secondaryScopeId = String(draftSwotData.secondary_position_id || selectedSecondaryPositionId || "").trim();
                        setSelectedPrimaryPositionId(primaryScopeId);
                        setSelectedSecondaryPositionId(secondaryScopeId);
                        const scopeSource = Array.isArray(draftScopeOptions) && draftScopeOptions.length > 0
                            ? draftScopeOptions
                            : positionOptions;
                        if (Array.isArray(scopeSource) && scopeSource.length > 0) {
                            const ordered = buildLaneOptionsFromSelections(scopeSource, primaryScopeId, secondaryScopeId);
                            setPositionOptions(ordered);
                        }
                        const laneScope = primaryScopeId || secondaryScopeId;
                        let rowSwot = normalizeSwotState({
                            strengths: draftSwotData.strengths || [],
                            weaknesses: draftSwotData.weaknesses || [],
                            opportunities: draftSwotData.opportunities || [],
                            threats: draftSwotData.threats || []
                        });
                        const draftPlayerTypeId = String(draftSwotData?.player_type || "").trim();
                        if (isSwotStateBlank(rowSwot) && draftPlayerTypeId) {
                            const seeded = await seedDraftSwotFromPlayerType({
                                targetProgramId,
                                playerTypeId: draftPlayerTypeId,
                                draftEmail,
                                draftName
                            });
                            if (seeded) {
                                const { data: refreshedDraftSwotData, error: refreshedDraftSwotError } = await supabase
                                    .from("athlete_swot")
                                    .select("program_id, prebuilt_name, prebuilt_email, position_id, secondary_position_id, player_type, strengths, weaknesses, opportunities, threats, status")
                                    .eq("id", draftSwotId)
                                    .is("athlete_id", null)
                                    .maybeSingle();
                                if (refreshedDraftSwotError) throw refreshedDraftSwotError;
                                if (refreshedDraftSwotData) {
                                    rowSwot = normalizeSwotState({
                                        strengths: refreshedDraftSwotData.strengths || [],
                                        weaknesses: refreshedDraftSwotData.weaknesses || [],
                                        opportunities: refreshedDraftSwotData.opportunities || [],
                                        threats: refreshedDraftSwotData.threats || []
                                    });
                                }
                            }
                        }
                        if (draftEmail) {
                            const { data: draftRows } = await supabase
                                .from("athlete_swot")
                                .select("position_id, secondary_position_id, player_type, strengths, weaknesses, opportunities, threats, updated_at")
                                .is("athlete_id", null)
                                .eq("program_id", targetProgramId)
                                .ilike("prebuilt_email", draftEmail)
                                .order("updated_at", { ascending: false });
                            if (Array.isArray(draftRows) && draftRows.length > 0) {
                                const primaryRow = draftRows.find((row) => row?.position_id);
                                const secondaryRow = draftRows.find((row) => row?.secondary_position_id);
                                primaryScopeId = String(primaryRow?.position_id || primaryScopeId || "").trim();
                                secondaryScopeId = String(secondaryRow?.secondary_position_id || secondaryScopeId || "").trim();
                                if (primaryScopeId) setSelectedPrimaryPositionId(primaryScopeId);
                                if (secondaryScopeId) setSelectedSecondaryPositionId(secondaryScopeId);
                                const scopeSource = Array.isArray(draftScopeOptions) && draftScopeOptions.length > 0
                                    ? draftScopeOptions
                                    : positionOptions;
                                if (Array.isArray(scopeSource) && scopeSource.length > 0) {
                                    const ordered = buildLaneOptionsFromSelections(scopeSource, primaryScopeId, secondaryScopeId);
                                    setPositionOptions(ordered);
                                }
                                const { primaryMap, secondaryMap } = buildPlayerTypeMaps(draftRows);
                                setPlayerTypeByPrimaryPositionId(primaryMap);
                                setPlayerTypeBySecondaryPositionId(secondaryMap);
                                const scoped = {};
                                draftRows.forEach((row) => {
                                    const scopeKey = String(row.position_id || row.secondary_position_id || "").trim();
                                    if (!scopeKey || scoped[scopeKey]) return;
                                    scoped[scopeKey] = normalizeSwotState({
                                        strengths: row.strengths || [],
                                        weaknesses: row.weaknesses || [],
                                        opportunities: row.opportunities || [],
                                        threats: row.threats || []
                                    });
                                });
                                setSwotByScope(scoped);
                                const selectedScope = laneScope || Object.keys(scoped)[0] || "";
                                setSelectedSwotPositionId(selectedScope);
                                setSwot(scoped[selectedScope] || rowSwot);
                            } else {
                                const initialScope = laneScope || String(selectedPrimaryPositionId || "").trim();
                                if (initialScope) {
                                    setSwotByScope({ [initialScope]: rowSwot });
                                    setSelectedSwotPositionId(initialScope);
                                }
                                setSwot(rowSwot);
                            }
                        } else {
                            const initialScope = laneScope || String(selectedPrimaryPositionId || "").trim();
                            if (initialScope) {
                                setSwotByScope({ [initialScope]: rowSwot });
                                setSelectedSwotPositionId(initialScope);
                            }
                            setSwot(rowSwot);
                        }
                        setDraftStatus(draftSwotData.status || "draft");
                    }
                    return;
                }

                const targetProgramIdFromState = location.state?.programId || null;
                const { data: athleteData, error: athError } = await fetchRosterRowForProgram(targetProgramIdFromState);

                if (athError) throw athError;

                setAthlete(athleteData.users);

                // 1. Capture the Program Name and Sport from state or query
                const targetProgramId = location.state?.programId || athleteData.team.programs.id;
                let targetSport = location.state?.sport || athleteData.team.programs.sport;
                let targetProgramName = location.state?.programName || athleteData.team.programs.name;
                if (location.state?.programId) {
                    const { data: programRow, error: programError } = await fetchProgramById(location.state?.programId);
                    if (!programError && programRow) {
                        targetSport = programRow.sport;
                        targetProgramName = programRow.name;
                    }
                }

                setProgram({ id: targetProgramId, sport: targetSport });
                setProgramName(targetProgramName); // Set the display name

                // ... rest of your data fetching (sections, items, existing swot) ...
                const { data: sportData } = await supabase.from("development_sports").select("id").eq("name", targetSport).single();
                if (sportData) {
                    const scopeOptions = await fetchPositionOptions(sportData.id);
                    const { data: sectionsData } = await supabase.from("development_sections").select("id, name").eq("sport_id", sportData.id).order("name");
                    setSections(sectionsData || []);
                    if (sectionsData?.length) {
                        const { data: itemsData } = await supabase.from("development_items").select("id, name, section_id").in("section_id", sectionsData.map(s => s.id)).order("name");
                        const grouped = {};
                        itemsData?.forEach(i => { if (!grouped[i.section_id]) grouped[i.section_id] = []; grouped[i.section_id].push(i); });
                        setItemsBySection(grouped);

                        const microData = await fetchMicroPointsForSections(sectionsData || []);
                        setMicroPointsBySection(microData.grouped);
                    }
                    await loadSwotScopesForProgram(targetProgramId, selectedSwotPositionId, scopeOptions, targetSport);
                }

            } catch (err) { console.error(err); } finally { setHasUnsavedSwotChanges(false); setLoading(false); }
        }
        fetchData();
    }, [athleteId, draftSwotId, isDraftSwot, location.state]);

    const [forms, setForms] = useState({
        strengths: { section: "", item: "" },
        weaknesses: { section: "", item: "" },
        opportunities: { section: "", item: "" },
        threats: { section: "", item: "" },
    });

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const swotDefinitions = {
        strengths: "What does this athlete do at a high level right now?",
        weaknesses: "What skill or habit is holding back the next step?",
        opportunities: "What adjustment would accelerate progress most?",
        threats: "What could get in the way of long-term progress?"
    };

    // Editable coach guidance shown under each section header.
    const swotHeaderSubtext = {
        strengths: "List all repeatable strengths the athlete can rely on in games.",
        weaknesses: "List all high-impact gaps that are coachable and measurable.",
        opportunities: "Add near-term growth skills and abilities that can unlock performance.",
        threats: "Capture risks that could limit progress if not addressed early."
    };

    useEffect(() => {
        if (isDraftSwot) return;
        if (!athleteId) return;
        async function fetchData() {
            try {
                const targetProgramIdFromState = location.state?.programId || null;
                const { data: athleteData, error: athError } = await fetchRosterRowForProgram(targetProgramIdFromState);
                if (athError) throw athError;

                setAthlete(athleteData.users);

                // Prioritize context from prop/state
                const targetProgramId = location.state?.programId || athleteData.team.programs.id;
                let targetSport = location.state?.sport || athleteData.team.programs.sport;
                if (location.state?.programId) {
                    const { data: programRow, error: programError } = await fetchProgramById(location.state?.programId);
                    if (!programError && programRow) {
                        targetSport = programRow.sport;
                    }
                }

                setProgram({ id: targetProgramId, sport: targetSport });

                const { data: sportData } = await supabase.from("development_sports").select("id").eq("name", targetSport).single();
                if (sportData) {
                    const scopeOptions = await fetchPositionOptions(sportData.id);
                    const { data: sectionsData } = await supabase.from("development_sections").select("id, name").eq("sport_id", sportData.id).order("name");
                    setSections(sectionsData || []);
                    if (sectionsData?.length) {
                        const { data: itemsData } = await supabase.from("development_items").select("id, name, section_id").in("section_id", sectionsData.map(s => s.id)).order("name");
                        const grouped = {};
                        itemsData?.forEach(i => { if (!grouped[i.section_id]) grouped[i.section_id] = []; grouped[i.section_id].push(i); });
                        setItemsBySection(grouped);

                        const microData = await fetchMicroPointsForSections(sectionsData || []);
                        setMicroPointsBySection(microData.grouped);
                    }
                    await loadSwotScopesForProgram(targetProgramId, selectedSwotPositionId, scopeOptions, targetSport);
                }
            } catch (err) { console.error(err); } finally { setHasUnsavedSwotChanges(false); setLoading(false); }
        }
        fetchData();
    }, [athleteId, isDraftSwot, location.state]);

    useEffect(() => {
        const scopeKey = String(selectedSwotPositionId || "").trim();
        if (!scopeKey) {
            if (Object.keys(swotByScope || {}).length === 0) {
                return;
            }
            setSwot(normalizeSwotState(EMPTY_SWOT_STATE));
            return;
        }
        const laneState = swotByScope[scopeKey] || normalizeSwotState(EMPTY_SWOT_STATE);
        setSwot(laneState);
        setPriorityBucketTab("skill");
        setPriorityError("");
        setExpandedPriorityIds([]);
    }, [selectedSwotPositionId, swotByScope]);

    useEffect(() => {
        const ordered = buildLaneOptionsFromSelections(
            allSportPositions,
            selectedPrimaryPositionId,
            selectedSecondaryPositionId
        );
        setPositionOptions(ordered);
        setSelectedSwotPositionId((prev) => {
            const valid = ordered.some((opt) => String(opt.id) === String(prev || ""));
            return valid ? prev : (ordered[0]?.id || "");
        });
    }, [allSportPositions, selectedPrimaryPositionId, selectedSecondaryPositionId]);

    const updateForm = (category, field, value) => {
        setForms(prev => {
            const f = { ...prev[category] };
            f[field] = value;
            if (field === 'section') f.item = "";
            return { ...prev, [category]: f };
        });
    };

    const handleAddItem = (category) => {
        if (!canEdit) return;
        const f = forms[category];
        if (!f.item) return;
        const selectedDevelopmentItem = (itemsBySection[f.section] || []).find((entry) => entry.id === f.item);
        const newItem = {
            id: uuidv4(),
            section: sections.find(s => s.id === f.section)?.name || "General",
            sectionId: f.section || null,
            developmentItemId: selectedDevelopmentItem?.id || null,
            item: selectedDevelopmentItem?.name || f.item,
            completed: false,
            priority: false,
            priorityRank: null,
            actions: []
        };
        applySwotUpdate(prev => ({ ...prev, [category]: [...prev[category], newItem] }));
        setForms(prev => ({ ...prev, [category]: { section: "", item: "" } }));
    };

    const handleRemoveItem = (category, id) => {
        if (!canEdit) return;
        applySwotUpdate(prev => {
        const next = { ...prev, [category]: prev[category].filter(o => o.id !== id) };
        const orderedIds = collectPriorityItems(next)
            .sort((a, b) => (a.priorityRank || 999) - (b.priorityRank || 999))
            .map((item) => item.id);
        return assignPriorityRanks(next, orderedIds);
    });
    };
    const handleUpdateItem = (id, cat, data) => {
        if (!canEdit) return;
        applySwotUpdate(prev => ({ ...prev, [cat]: prev[cat].map(i => i.id === id ? { ...i, ...data, completed: isItemCompleted({ ...i, ...data }) } : i) }));
    };
    const upsertPriorityActions = (entry, updater) => {
        const normalized = normalizeActions(entry.actions);
        while (normalized.length < 3) {
            normalized.push({
                microPointId: "",
                microPointTitle: "",
                tasks: [],
                is_completed: false,
                completed_at: null
            });
        }
        const updated = updater(normalized);
        const cleaned = updated
            .map((action) => {
                const tasks = (Array.isArray(action.tasks) ? action.tasks : [])
                    .map(normalizeChecklistTask);
                while (tasks.length < 1) {
                    tasks.push({ id: uuidv4(), text: "", is_completed: false, completed_at: null });
                }
                const enteredTasks = tasks.filter((task) => String(task.text || "").trim() !== "");
                const isCompleted = enteredTasks.length > 0 && enteredTasks.every((task) => Boolean(task.is_completed));
                return {
                    microPointId: String(action.microPointId || "").trim(),
                    microPointTitle: String(action.microPointTitle || "").trim(),
                    tasks,
                    is_completed: isCompleted,
                    completed_at: isCompleted ? (action.completed_at || new Date().toISOString()) : null
                };
            });
        while (cleaned.length < 3) {
            cleaned.push({
                microPointId: "",
                microPointTitle: "",
                tasks: [{ id: uuidv4(), text: "", is_completed: false, completed_at: null }],
                is_completed: false,
                completed_at: null
            });
        }
        const activeActions = cleaned.filter(isActionFilled);
        return {
            ...entry,
            actions: cleaned,
            completed: activeActions.length > 0 && activeActions.every((action) => action.is_completed)
        };
    };
    const handlePriorityMicroPointChange = (category, itemId, actionIndex, microPointId, options) => {
        if (!canEdit) return;
        const selectedOption = (options || []).find((option) => option.id === microPointId);
        applySwotUpdate((prev) => ({
            ...prev,
            [category]: prev[category].map((entry) => {
                if (entry.id !== itemId) return entry;
                return upsertPriorityActions(entry, (normalized) => {
                    normalized[actionIndex] = {
                        ...normalized[actionIndex],
                        microPointId: String(microPointId || "").trim(),
                        microPointTitle: selectedOption?.title || ""
                    };
                    return normalized;
                });
            })
        }));
    };
    const handleUpdateChecklistTaskText = (category, itemId, actionIndex, taskIndex, text) => {
        if (!canEdit) return;
        applySwotUpdate((prev) => ({
            ...prev,
            [category]: prev[category].map((entry) => {
                if (entry.id !== itemId) return entry;
                return upsertPriorityActions(entry, (normalized) => {
                    const tasks = Array.isArray(normalized[actionIndex].tasks) ? normalized[actionIndex].tasks : [];
                    while (tasks.length < 1) {
                        tasks.push({ id: uuidv4(), text: "", is_completed: false, completed_at: null });
                    }
                    normalized[actionIndex] = {
                        ...normalized[actionIndex],
                        tasks: tasks.map((task, idx) => idx === taskIndex ? { ...task, text: text === "" ? null : text } : task)
                    };
                    return normalized;
                });
            })
        }));
    };
    const handleAddChecklistTask = (category, itemId, actionIndex) => {
        if (!canEdit) return;
        applySwotUpdate((prev) => ({
            ...prev,
            [category]: prev[category].map((entry) => {
                if (entry.id !== itemId) return entry;
                return upsertPriorityActions(entry, (normalized) => {
                    const tasks = Array.isArray(normalized[actionIndex].tasks) ? [...normalized[actionIndex].tasks] : [];
                    tasks.push({ id: uuidv4(), text: "", is_completed: false, completed_at: null });
                    normalized[actionIndex] = {
                        ...normalized[actionIndex],
                        tasks
                    };
                    return normalized;
                });
            })
        }));
    };
    const handleRemoveChecklistTask = (category, itemId, actionIndex, taskIndex) => {
        if (!canEdit) return;
        applySwotUpdate((prev) => ({
            ...prev,
            [category]: prev[category].map((entry) => {
                if (entry.id !== itemId) return entry;
                return upsertPriorityActions(entry, (normalized) => {
                    const tasks = Array.isArray(normalized[actionIndex].tasks) ? [...normalized[actionIndex].tasks] : [];
                    let nextTasks = tasks
                        .filter((_, idx) => idx !== taskIndex)
                        .map(normalizeChecklistTask);
                    if (nextTasks.length === 0) {
                        nextTasks = [{ id: uuidv4(), text: null, is_completed: false, completed_at: null }];
                    }
                    normalized[actionIndex] = {
                        ...normalized[actionIndex],
                        tasks: nextTasks
                    };
                    return normalized;
                });
            })
        }));
    };
    const handleDragEnd = (cat, { active, over }) => {
        if (!canEdit) return;
        if (!over || active.id === over.id) return;
        applySwotUpdate(prev => {
            const items = prev[cat];
            return { ...prev, [cat]: arrayMove(items, items.findIndex(i => i.id === active.id), items.findIndex(i => i.id === over.id)) };
        });
    };

    const priorityItems = collectPriorityItems(swot).sort((a, b) => (a.priorityRank || 999) - (b.priorityRank || 999));
    const groupedPriorityItems = priorityItems.reduce((acc, item) => {
        const bucket = isAthleticPriorityEntry(item) ? "athletic" : "skill";
        acc[bucket].push(item);
        return acc;
    }, { skill: [], athletic: [] });
    const visiblePriorityItems = groupedPriorityItems[priorityBucketTab] || [];

    const handleTogglePriority = (category, itemId) => {
        if (!canEdit) return;
        setPriorityError("");
        applySwotUpdate((prev) => {
            const priorities = collectPriorityItems(prev).sort((a, b) => (a.priorityRank || 999) - (b.priorityRank || 999));
            const existing = priorities.find((item) => item.id === itemId);
            const targetItem = (prev?.[category] || []).find((item) => item.id === itemId);
            const targetBucket = isAthleticPriorityEntry({ ...(targetItem || {}), category }) ? "athletic" : "skill";
            const bucketCount = priorities.filter((item) => {
                const bucket = isAthleticPriorityEntry(item) ? "athletic" : "skill";
                return bucket === targetBucket;
            }).length;

            if (!existing && bucketCount >= 3) {
                setPriorityError(`You can choose up to 3 ${targetBucket} season priorities.`);
                return prev;
            }

            const next = Object.fromEntries(
                Object.entries(prev).map(([cat, items]) => ([
                    cat,
                    (items || []).map((item) => {
                        if (cat !== category || item.id !== itemId) return item;
                        return { ...item, priority: !item.priority };
                    })
                ]))
            );

            const nextPriorityIds = collectPriorityItems(next)
                .sort((a, b) => (a.priorityRank || 999) - (b.priorityRank || 999))
                .map((item) => item.id);
            if (!existing) nextPriorityIds.push(itemId);
            if (existing) {
                setExpandedPriorityIds((prevExpanded) => prevExpanded.filter((id) => id !== itemId));
            }
            return assignPriorityRanks(next, nextPriorityIds);
        });
    };
    const handleTogglePriorityExpanded = (itemId) => {
        setExpandedPriorityIds((prev) =>
            prev.includes(itemId)
                ? prev.filter((id) => id !== itemId)
                : [...prev, itemId]
        );
    };

    const handlePriorityDragEnd = ({ active, over }) => {
        if (!canEdit) return;
        if (!over || active.id === over.id) return;
        applySwotUpdate((prev) => {
            const current = collectPriorityItems(prev).sort((a, b) => (a.priorityRank || 999) - (b.priorityRank || 999));
            const visibleIds = current
                .filter((item) => (isAthleticPriorityEntry(item) ? "athletic" : "skill") === priorityBucketTab)
                .map((item) => item.id);
            const oldIndex = visibleIds.findIndex((id) => id === active.id);
            const newIndex = visibleIds.findIndex((id) => id === over.id);
            if (oldIndex < 0 || newIndex < 0) return prev;
            const movedVisibleIds = arrayMove(visibleIds, oldIndex, newIndex);
            let visibleCursor = 0;
            const mergedIds = current.map((item) => {
                const bucket = isAthleticPriorityEntry(item) ? "athletic" : "skill";
                if (bucket !== priorityBucketTab) return item.id;
                const nextId = movedVisibleIds[visibleCursor];
                visibleCursor += 1;
                return nextId;
            });
            return assignPriorityRanks(prev, mergedIds);
        });
    };

    const handleSavePositionConfig = async () => {
        if (!canEdit || !selectedSportId) return;
        const primary = String(selectedPrimaryPositionId || "").trim();
        const secondaryRaw = String(selectedSecondaryPositionId || "").trim();
        const secondary = secondaryRaw && secondaryRaw !== primary ? secondaryRaw : null;
        setSavingPositionConfig(true);
        try {
            if (isDraftSwot || !athleteId) {
                const targetProgramId = location.state?.programId || program?.id || null;
                const draftEmail = String(draftPrebuiltEmail || location.state?.prebuiltEmail || "").trim();
                const draftName = String(location.state?.prebuiltName || athlete?.full_name || "").trim();
                const selectedPositionIds = Array.from(new Set([primary, secondary].filter(Boolean)));

                if (targetProgramId && draftEmail) {
                    const { data: existingDraftRows, error: existingDraftError } = await supabase
                        .from("athlete_swot")
                        .select("id, position_id, secondary_position_id, strengths, weaknesses, opportunities, threats, status")
                        .is("athlete_id", null)
                        .eq("program_id", targetProgramId)
                        .ilike("prebuilt_email", draftEmail)
                        .order("updated_at", { ascending: false });
                    if (existingDraftError) throw existingDraftError;

                    const sharedRow = (existingDraftRows || []).find((row) => !row?.position_id && !row?.secondary_position_id) || null;
                    const existingPositionRows = (existingDraftRows || []).filter((row) => Boolean(row?.position_id || row?.secondary_position_id));
                    const baseState = sharedRow
                        ? normalizeSwotState({
                            strengths: sharedRow.strengths || [],
                            weaknesses: sharedRow.weaknesses || [],
                            opportunities: sharedRow.opportunities || [],
                            threats: sharedRow.threats || []
                        })
                        : normalizeSwotState(swot);

                    const obsoleteRowIds = existingPositionRows
                        .filter((row) => !selectedPositionIds.includes(String(row.position_id || row.secondary_position_id || "")))
                        .map((row) => row.id)
                        .filter(Boolean);

                    if (obsoleteRowIds.length > 0) {
                        const { error: deleteDraftRowsError } = await supabase
                            .from("athlete_swot")
                            .delete()
                            .in("id", obsoleteRowIds)
                            .is("athlete_id", null);
                        if (deleteDraftRowsError) throw deleteDraftRowsError;
                    }

                    console.log("[SWOTPage] Save Positions:start", {
                        targetProgramId,
                        draftEmail,
                        primary,
                        secondary,
                        selectedPositionIds,
                        existingPositionIds: existingPositionRows.map((row) => String(row.position_id || row.secondary_position_id || "")),
                        deletedPositionRowIds: obsoleteRowIds
                    });

                    for (let index = 0; index < selectedPositionIds.length; index += 1) {
                        const positionId = selectedPositionIds[index];
                        const existingRow = (existingDraftRows || []).find((row) =>
                            String(row.position_id || row.secondary_position_id || "") === String(positionId)
                        );
                        const rowTimestamp = new Date(Date.now() + index).toISOString();
                        const scopeColumns = getScopeColumns(positionId);
                        const payload = {
                            athlete_id: null,
                            program_id: targetProgramId,
                            position_id: scopeColumns.position_id,
                            secondary_position_id: scopeColumns.secondary_position_id,
                            strengths: baseState.strengths,
                            weaknesses: baseState.weaknesses,
                            opportunities: baseState.opportunities,
                            threats: baseState.threats,
                            status: existingRow?.status || draftStatus || "draft",
                            prebuilt_name: draftName || null,
                            prebuilt_email: draftEmail,
                            updated_at: rowTimestamp
                        };

                        if (existingRow?.id) {
                            const { error: updateDraftError } = await supabase
                                .from("athlete_swot")
                                .update(payload)
                                .eq("id", existingRow.id)
                                .is("athlete_id", null);
                            if (updateDraftError) throw updateDraftError;
                        } else {
                            const { error: insertDraftError } = await supabase
                                .from("athlete_swot")
                                .insert([payload]);
                            if (insertDraftError) throw insertDraftError;
                        }
                    }

                    const { data: persistedDraftRows, error: persistedDraftRowsError } = await supabase
                        .from("athlete_swot")
                        .select("id, position_id, secondary_position_id, updated_at")
                        .is("athlete_id", null)
                        .eq("program_id", targetProgramId)
                        .ilike("prebuilt_email", draftEmail)
                        .order("updated_at", { ascending: false });
                    if (persistedDraftRowsError) throw persistedDraftRowsError;

                    console.log("[SWOTPage] Save Positions:done", {
                        draftEmail,
                        selectedPositionIds,
                        persistedPositionIds: (persistedDraftRows || [])
                            .map((row) => String(row.position_id || row.secondary_position_id || "").trim())
                            .filter(Boolean),
                        persistedRows: persistedDraftRows || []
                    });

                    await fetchPositionOptions(selectedSportId, {
                        targetProgramId,
                        draftEmail
                    });
                }
                alert("Draft positions updated.");
                return;
            }

            const { data: existingRow, error: existingError } = await supabase
                .from("user_sport_info")
                .select("id")
                .eq("user_id", athleteId)
                .eq("sport", selectedSportId)
                .maybeSingle();
            if (existingError) throw existingError;

            if (existingRow?.id) {
                const { error: updateError } = await supabase
                    .from("user_sport_info")
                    .update({
                        position: primary || null,
                        secondary_position: secondary
                    })
                    .eq("id", existingRow.id);
                if (updateError) throw updateError;
            } else {
                const { error: insertError } = await supabase
                    .from("user_sport_info")
                    .insert({
                        user_id: athleteId,
                        sport: selectedSportId,
                        position: primary || null,
                        secondary_position: secondary
                    });
                if (insertError) throw insertError;
            }

            const ordered = buildLaneOptionsFromSelections(allSportPositions, primary, secondary);
            setPositionOptions(ordered);
            setSelectedSwotPositionId((prev) => {
                const valid = ordered.some((opt) => String(opt.id) === String(prev || ""));
                return valid ? prev : (ordered[0]?.id || "");
            });
            alert("Position preferences updated.");
        } catch (err) {
            console.error("Error saving position preferences:", err);
            alert("Failed to update position preferences.");
        } finally {
            setSavingPositionConfig(false);
        }
    };

    const handleSave = async (statusOverride = null) => {
        if (!canEdit) return;
        if (!program) return;
        setIsSaving(true);
        try {
            if (isDraftSwot && draftSwotId) {
                const resolvedDraftStatus = statusOverride || draftStatus;
                const mergedByScope = {
                    ...swotByScope,
                    ...(selectedSwotPositionId ? { [selectedSwotPositionId]: normalizeSwotState(swot) } : {})
                };
                const scopeKeys = Array.from(new Set([
                    ...positionOptions.map((opt) => String(opt.id)),
                    ...Object.keys(mergedByScope)
                ])).filter(Boolean);
                const draftEmail = String(draftPrebuiltEmail || location.state?.prebuiltEmail || "").trim();
                const draftName = String(location.state?.prebuiltName || athlete?.full_name || "").trim();
                const athleticByScope = scopeKeys.map((scopeKey) => (
                    splitSwotByAthleticBucket(mergedByScope[scopeKey] || EMPTY_SWOT_STATE).athletic
                ));
                const sharedAthleticState = athleticByScope.reduce(
                    (acc, state) => mergeAthleticStates(acc, state),
                    normalizeSwotState(EMPTY_SWOT_STATE)
                );
                const skillOnlyByScope = Object.fromEntries(
                    scopeKeys.map((scopeKey) => ([
                        scopeKey,
                        splitSwotByAthleticBucket(mergedByScope[scopeKey] || EMPTY_SWOT_STATE).skill
                    ]))
                );

                for (const scopeKey of scopeKeys) {
                    const scopeSwot = normalizeSwotState(skillOnlyByScope[scopeKey] || EMPTY_SWOT_STATE);
                    const scopedWithPosition = annotateSwotItemsWithPositionId(
                        scopeSwot,
                        scopeKeyToPositionId(scopeKey)
                    );
                    const scopeColumns = getScopeColumns(scopeKey);
                    const rowPayload = {
                        athlete_id: null,
                        program_id: program?.id || location.state?.programId || null,
                        position_id: scopeColumns.position_id,
                        secondary_position_id: scopeColumns.secondary_position_id,
                        strengths: scopedWithPosition.strengths,
                        weaknesses: scopedWithPosition.weaknesses,
                        opportunities: scopedWithPosition.opportunities,
                        threats: scopedWithPosition.threats,
                        status: resolvedDraftStatus === "published" ? "published" : "draft",
                        prebuilt_name: draftName || null,
                        prebuilt_email: draftEmail || null,
                        updated_at: new Date()
                    };
                    if (!rowPayload.program_id) continue;

                    let updateQuery = supabase
                        .from("athlete_swot")
                        .update({
                            strengths: rowPayload.strengths,
                            weaknesses: rowPayload.weaknesses,
                            opportunities: rowPayload.opportunities,
                            threats: rowPayload.threats,
                            status: rowPayload.status,
                            prebuilt_name: rowPayload.prebuilt_name,
                            prebuilt_email: rowPayload.prebuilt_email,
                            updated_at: rowPayload.updated_at
                        })
                        .eq("program_id", rowPayload.program_id)
                        .is("athlete_id", null);
                    updateQuery = rowPayload.prebuilt_email
                        ? updateQuery.ilike("prebuilt_email", rowPayload.prebuilt_email)
                        : updateQuery.eq("id", draftSwotId);
                    if (rowPayload.secondary_position_id) {
                        updateQuery = updateQuery.is("position_id", null).eq("secondary_position_id", rowPayload.secondary_position_id);
                    } else if (rowPayload.position_id) {
                        updateQuery = updateQuery.eq("position_id", rowPayload.position_id).is("secondary_position_id", null);
                    } else {
                        updateQuery = updateQuery.is("position_id", null).is("secondary_position_id", null);
                    }
                    const { data: updatedRows, error: updateError } = await updateQuery.select("id");
                    if (updateError) throw updateError;

                    if (!updatedRows || updatedRows.length === 0) {
                        const { error: insertError } = await supabase
                            .from("athlete_swot")
                            .insert(rowPayload);
                        if (insertError) throw insertError;
                    }
                }

                const sharedAthleticWithPosition = annotateSwotItemsWithPositionId(sharedAthleticState, null);
                const sharedPayload = {
                    athlete_id: null,
                    program_id: program?.id || location.state?.programId || null,
                    position_id: null,
                    secondary_position_id: null,
                    strengths: sharedAthleticWithPosition.strengths,
                    weaknesses: sharedAthleticWithPosition.weaknesses,
                    opportunities: sharedAthleticWithPosition.opportunities,
                    threats: sharedAthleticWithPosition.threats,
                    status: resolvedDraftStatus === "published" ? "published" : "draft",
                    prebuilt_name: draftName || null,
                    prebuilt_email: draftEmail || null,
                    updated_at: new Date()
                };
                if (sharedPayload.program_id) {
                    let sharedUpdateQuery = supabase
                        .from("athlete_swot")
                    .update({
                        strengths: sharedPayload.strengths,
                        weaknesses: sharedPayload.weaknesses,
                        opportunities: sharedPayload.opportunities,
                        threats: sharedPayload.threats,
                        status: sharedPayload.status,
                        prebuilt_name: sharedPayload.prebuilt_name,
                        prebuilt_email: sharedPayload.prebuilt_email,
                        updated_at: sharedPayload.updated_at
                    })
                    .eq("program_id", sharedPayload.program_id)
                    .is("athlete_id", null)
                    .is("position_id", null)
                    .is("secondary_position_id", null);
                    sharedUpdateQuery = sharedPayload.prebuilt_email
                        ? sharedUpdateQuery.ilike("prebuilt_email", sharedPayload.prebuilt_email)
                        : sharedUpdateQuery.eq("id", draftSwotId);
                    const { data: sharedUpdatedRows, error: sharedUpdateError } = await sharedUpdateQuery.select("id");
                    if (sharedUpdateError) throw sharedUpdateError;
                    if (!sharedUpdatedRows || sharedUpdatedRows.length === 0) {
                        const { error: sharedInsertError } = await supabase
                            .from("athlete_swot")
                            .insert(sharedPayload);
                        if (sharedInsertError) throw sharedInsertError;
                    }
                }
                if (resolvedDraftStatus === "published" && draftStatus !== "published") {
                    setDraftStatus("published");
                }
                setHasUnsavedSwotChanges(false);
                alert(resolvedDraftStatus === "published" ? "Published SWOT saved." : "Draft SWOT saved.");
                return;
            }

            if (!athleteId) return;

            const swotIsBlank = isSwotStateBlank(swot) &&
                Object.values(swotByScope || {}).every((scope) => isSwotStateBlank(scope));

            if (swotIsBlank) {
                const { data: existingRows, error: existingError } = await supabase
                    .from("athlete_swot")
                    .select("id")
                    .eq("athlete_id", athleteId)
                    .eq("program_id", program.id)
                    .limit(1);
                if (existingError) throw existingError;

                if (!existingRows || existingRows.length === 0) {
                    const { data: sportRow, error: sportError } = await supabase
                        .from("development_sports")
                        .select("id")
                        .eq("name", program.sport)
                        .maybeSingle();
                    if (sportError) throw sportError;

                    if (sportRow?.id) {
                        const { data: infoRow, error: infoError } = await supabase
                            .from("user_sport_info")
                            .select("player_type")
                            .eq("user_id", athleteId)
                            .eq("sport", sportRow.id)
                            .maybeSingle();
                        if (infoError) throw infoError;

                        const playerTypeId = String(infoRow?.player_type || "").trim();
                        if (playerTypeId) {
                            const seedPayload = await buildSwotPayloadFromPlayerType(playerTypeId);
                            if (seedPayload) {
                                const { error: insertError } = await supabase
                                    .from("athlete_swot")
                                    .insert([{
                                        athlete_id: athleteId,
                                        program_id: program.id,
                                        position_id: null,
                                        strengths: seedPayload.strengths,
                                        weaknesses: seedPayload.weaknesses,
                                        opportunities: seedPayload.opportunities,
                                        threats: seedPayload.threats,
                                        status: "published",
                                        prebuilt_name: null,
                                        prebuilt_email: null,
                                        updated_at: new Date()
                                    }]);
                                if (insertError) throw insertError;
                                setHasProgramSwot(true);
                                setHasUnsavedSwotChanges(false);
                                setSwot(normalizeSwotState(seedPayload));
                                alert("Saved successfully!");
                                return;
                            }
                        }
                    }
                }
            }

            const mergedByScope = {
                ...swotByScope,
                ...(selectedSwotPositionId ? { [selectedSwotPositionId]: normalizeSwotState(swot) } : {})
            };
            const scopeKeys = Array.from(new Set([
                ...positionOptions.map((opt) => String(opt.id)),
                ...Object.keys(mergedByScope)
            ]));
            const athleticByScope = scopeKeys.map((scopeKey) => (
                splitSwotByAthleticBucket(mergedByScope[scopeKey] || EMPTY_SWOT_STATE).athletic
            ));
            const sharedAthleticState = athleticByScope.reduce(
                (acc, state) => mergeAthleticStates(acc, state),
                normalizeSwotState(EMPTY_SWOT_STATE)
            );
            const skillOnlyByScope = Object.fromEntries(
                scopeKeys.map((scopeKey) => ([
                    scopeKey,
                    splitSwotByAthleticBucket(mergedByScope[scopeKey] || EMPTY_SWOT_STATE).skill
                ]))
            );

            const rows = scopeKeys.map((scopeKey) => {
                const scopeSwot = normalizeSwotState(skillOnlyByScope[scopeKey] || EMPTY_SWOT_STATE);
                const scopedWithPosition = annotateSwotItemsWithPositionId(
                    scopeSwot,
                    scopeKeyToPositionId(scopeKey)
                );
                const scopeColumns = getScopeColumns(scopeKey);
                return {
                    athlete_id: athleteId,
                    program_id: program.id,
                    position_id: scopeColumns.position_id,
                    secondary_position_id: scopeColumns.secondary_position_id,
                    strengths: scopedWithPosition.strengths,
                    weaknesses: scopedWithPosition.weaknesses,
                    opportunities: scopedWithPosition.opportunities,
                    threats: scopedWithPosition.threats,
                    status: "published",
                    prebuilt_name: null,
                    prebuilt_email: null,
                    updated_at: new Date()
                };
            });
            for (const row of rows) {
                let updateQuery = supabase
                    .from("athlete_swot")
                    .update({
                        strengths: row.strengths,
                        weaknesses: row.weaknesses,
                        opportunities: row.opportunities,
                        threats: row.threats,
                        status: row.status,
                        prebuilt_name: row.prebuilt_name,
                        prebuilt_email: row.prebuilt_email,
                        updated_at: row.updated_at
                    })
                    .eq("athlete_id", row.athlete_id)
                    .eq("program_id", row.program_id);
                if (row.secondary_position_id) {
                    updateQuery = updateQuery.is("position_id", null).eq("secondary_position_id", row.secondary_position_id);
                } else if (row.position_id) {
                    updateQuery = updateQuery.eq("position_id", row.position_id).is("secondary_position_id", null);
                } else {
                    updateQuery = updateQuery.is("position_id", null).is("secondary_position_id", null);
                }
                const { data: updatedRows, error: updateError } = await updateQuery.select("id");
                if (updateError) throw updateError;

                if (!updatedRows || updatedRows.length === 0) {
                    const { error: insertError } = await supabase
                        .from("athlete_swot")
                        .insert(row);
                    if (insertError) throw insertError;
                }
            }

            const sharedAthleticWithPosition = annotateSwotItemsWithPositionId(sharedAthleticState, null);
            const sharedRow = {
                athlete_id: athleteId,
                program_id: program.id,
                position_id: null,
                secondary_position_id: null,
                strengths: sharedAthleticWithPosition.strengths,
                weaknesses: sharedAthleticWithPosition.weaknesses,
                opportunities: sharedAthleticWithPosition.opportunities,
                threats: sharedAthleticWithPosition.threats,
                status: "published",
                prebuilt_name: null,
                prebuilt_email: null,
                updated_at: new Date()
            };
            const { data: updatedSharedRows, error: updateSharedError } = await supabase
                .from("athlete_swot")
                .update({
                    strengths: sharedRow.strengths,
                    weaknesses: sharedRow.weaknesses,
                    opportunities: sharedRow.opportunities,
                    threats: sharedRow.threats,
                    status: sharedRow.status,
                    prebuilt_name: null,
                    prebuilt_email: null,
                    updated_at: sharedRow.updated_at
                })
                .eq("athlete_id", sharedRow.athlete_id)
                .eq("program_id", sharedRow.program_id)
                .is("position_id", null)
                .is("secondary_position_id", null)
                .select("id");
            if (updateSharedError) throw updateSharedError;
            if (!updatedSharedRows || updatedSharedRows.length === 0) {
                const { error: insertSharedError } = await supabase
                    .from("athlete_swot")
                    .insert(sharedRow);
                if (insertSharedError?.code === "23505") {
                    const { error: fallbackUpdateError } = await supabase
                        .from("athlete_swot")
                    .update({
                        strengths: sharedRow.strengths,
                        weaknesses: sharedRow.weaknesses,
                        opportunities: sharedRow.opportunities,
                        threats: sharedRow.threats,
                        status: sharedRow.status,
                        prebuilt_name: null,
                        prebuilt_email: null,
                        updated_at: sharedRow.updated_at
                    })
                    .eq("athlete_id", sharedRow.athlete_id)
                    .eq("program_id", sharedRow.program_id)
                    .is("position_id", null)
                    .is("secondary_position_id", null);
                    if (fallbackUpdateError) throw fallbackUpdateError;
                } else if (insertSharedError) {
                    throw insertSharedError;
                }
            }
            setHasUnsavedSwotChanges(false);
            alert("Saved successfully!");
        } catch (err) { alert("Save failed."); } finally { setIsSaving(false); }
    };

    const handlePublish = async () => {
        await handleSave("published");
    };

    if (loading) return <div className="p-10 text-center text-gray-500">Loading SWOT Profile...</div>;

    return (
        <div className="px-3 py-4 sm:p-6 max-w-7xl mx-auto font-sans text-gray-800">
            {canEdit ? (
                <DetailModal item={activeItem} sections={sections} onClose={() => setActiveItem(null)} onSave={handleUpdateItem} />
            ) : null}

            <div className="mb-3">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors border border-gray-200 rounded-full px-3 py-1.5 bg-white shadow-sm"
                >
                    Back
                </button>
            </div>

            <div className="relative mb-4 sm:mb-6 rounded-2xl border border-slate-200/70 bg-gradient-to-r from-slate-50 to-white p-4 sm:p-6 shadow-sm">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-4">
                    <div className="w-full lg:w-auto">
                        {isDraftSwot && draftStatus !== "published" ? (
                            <div className="mb-2">
                                <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide">
                                    Draft
                                </span>
                            </div>
                        ) : null}
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">{athlete?.full_name}</h1>
                        <p className="text-sm text-gray-500 mt-1">SWOT Analysis for <span className="font-semibold text-blue-600">{programName || "Loading Program..."}</span></p>
                    </div>
                </div>
                <div className="flex flex-col items-start lg:items-end gap-3 w-full lg:w-auto lg:justify-end">
                    <button
                        type="button"
                        onClick={handleOpenOtherPrograms}
                        className="inline-flex items-center text-[11px] font-bold uppercase tracking-wide text-gray-500 hover:text-gray-900 transition"
                    >
                        View Other Programs
                    </button>
                    {canEdit && (!isDraftSwot || draftStatus === "published" || hasUnsavedSwotChanges) ? (
                        <button
                            onClick={handleSave}
                            disabled={isSaving || (isDraftSwot && draftStatus === "published" && !hasUnsavedSwotChanges)}
                            className={`w-full sm:w-auto px-6 py-2.5 font-semibold rounded-lg shadow transition ${(isSaving || (isDraftSwot && draftStatus === "published" && !hasUnsavedSwotChanges))
                                ? "bg-gray-400 text-white"
                                : "bg-blue-600 hover:bg-blue-700 text-white"
                                }`}
                        >
                            {isSaving ? "Saving..." : "Save Changes"}
                        </button>
                    ) : null}
                </div>
                <button
                    type="button"
                    onClick={() => setIsSwotTipsModalOpen(true)}
                    className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full border border-sky-200 bg-white text-xs font-black text-sky-700 shadow-sm"
                    aria-label="Open SWOT tips"
                >
                    i
                </button>
                {isDraftSwot && canEdit ? (
                    <button
                        type="button"
                        onClick={draftStatus === "published" ? undefined : handlePublish}
                        disabled={isSaving || draftStatus === "published"}
                        className={`absolute top-3 right-12 px-3 py-1.5 text-[11px] font-bold uppercase rounded-full shadow transition ${draftStatus === "published"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 cursor-default"
                            : (isSaving
                                ? "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
                                : "border-emerald-200 bg-emerald-600 text-white hover:bg-emerald-700 hover:border-emerald-700")
                            }`}
                    >
                        {draftStatus === "published" ? "Published" : "Publish"}
                    </button>
                ) : null}
            </div>

            {positionOptions.length > 0 ? (
                <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-4">
                    <div className="flex-1">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Positions</label>
                        <div className="flex flex-wrap gap-2">
                            {(() => {
                                const primaryId = String(selectedPrimaryPositionId || "").trim();
                                const secondaryId = String(selectedSecondaryPositionId || "").trim();
                                const primaryType = primaryId ? playerTypeByPrimaryPositionId[String(primaryId)] : "";
                                const secondaryType = secondaryId ? playerTypeBySecondaryPositionId[String(secondaryId)] : "";
                                const shouldCombine = primaryId && secondaryId && primaryType && secondaryType &&
                                    String(primaryType) === String(secondaryType);
                                if (!shouldCombine) {
                                    return positionOptions.map((option) => (
                                        <button
                                            key={option.id}
                                            type="button"
                                            onClick={() => setSelectedSwotPositionId(String(option.id))}
                                            className={`group rounded-xl border px-3 py-2 text-left transition ${String(selectedSwotPositionId) === String(option.id)
                                                ? "border-blue-600 bg-blue-600 text-white shadow-sm"
                                                : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                                                }`}
                                        >
                                            <div className="text-xs font-bold uppercase tracking-wide">
                                                {option.label}
                                            </div>
                                            <div className={`text-[10px] font-semibold ${String(selectedSwotPositionId) === String(option.id)
                                                ? "text-blue-100"
                                                : "text-gray-400"
                                                }`}
                                            >
                                                {option.label.toLowerCase().includes("secondary")
                                                    ? (getPlayerTypeName(playerTypeBySecondaryPositionId[String(option.id)]) || "Player Type: Not set")
                                                    : (getPlayerTypeName(playerTypeByPrimaryPositionId[String(option.id)]) || "Player Type: Not set")
                                                }
                                            </div>
                                        </button>
                                    ));
                                }

                                const byId = new Map(positionOptions.map((opt) => [String(opt.id), opt]));
                                const primaryLabel = byId.get(primaryId)?.label || "Primary";
                                const secondaryLabel = byId.get(secondaryId)?.label || "Secondary";
                                const combinedLabel = `${primaryLabel} / ${secondaryLabel}`;
                                const isActive = String(selectedSwotPositionId) === primaryId || String(selectedSwotPositionId) === secondaryId;
                                return (
                                    <button
                                        key="combined"
                                        type="button"
                                        onClick={() => setSelectedSwotPositionId(primaryId)}
                                        className={`group rounded-xl border px-3 py-2 text-left transition ${isActive
                                            ? "border-blue-600 bg-blue-600 text-white shadow-sm"
                                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                                            }`}
                                    >
                                        <div className="text-xs font-bold uppercase tracking-wide">
                                            {combinedLabel}
                                        </div>
                                        <div className={`text-[10px] font-semibold ${isActive
                                            ? "text-blue-100"
                                            : "text-gray-400"
                                            }`}
                                        >
                                            {getPlayerTypeName(primaryType) || "Player Type: Not set"}
                                        </div>
                                    </button>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            ) : null}

            {isOtherProgramsOpen ? (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                            <h3 className="text-base font-black text-gray-900 tracking-tight">Other Program SWOTs</h3>
                            <button
                                type="button"
                                onClick={() => setIsOtherProgramsOpen(false)}
                                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition"
                                aria-label="Close other programs modal"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="px-5 py-4">
                            {otherProgramSwots.length === 0 ? (
                                <p className="text-sm text-gray-500">No SWOTs from other programs yet.</p>
                            ) : (
                                <div className="space-y-3">
                                    {otherProgramSwots.map((row) => (
                                        <div key={row.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-gray-200 rounded-xl p-3">
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900">{row.programs?.name || "Unknown Program"}</p>
                                                <p className="text-xs text-gray-500">
                                                    {row.programs?.sport || "Sport"} · {row.status || "draft"} · {row.updated_at ? new Date(row.updated_at).toLocaleDateString() : "No date"}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleViewOtherProgram(row)}
                                                className="px-3 py-2 text-xs font-bold uppercase rounded-md border border-gray-200 text-gray-700 hover:bg-gray-900 hover:text-white hover:border-gray-900 transition"
                                            >
                                                View
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="px-5 py-4 border-t border-gray-100 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setIsOtherProgramsOpen(false)}
                                className="px-4 py-2 rounded-lg text-xs font-bold bg-gray-900 text-white hover:bg-black transition"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {isSwotTipsModalOpen ? (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-auto">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                            <h3 className="text-base font-black text-gray-900 tracking-tight">Administrator SWOT Tips</h3>
                            <button
                                type="button"
                                onClick={() => setIsSwotTipsModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition"
                                aria-label="Close tips modal"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="px-5 py-4">
                            <p className="text-sm text-gray-600 mb-3">
                                Build SWOTs for action, not volume. Keep entries tight and directly connected to development plans.
                            </p>
                            <ul className="space-y-2 text-sm text-gray-800">
                                <li>1. Add only observable items that can be validated in film, training, or match data.</li>
                                <li>2. Keep each category concise: prioritize high-impact items over long lists.</li>
                                <li>3. Use Season Priorities to select the top 3 skill and top 3 athletic focus points.</li>
                                <li>4. For each priority, attach micro-point actions with concrete task checklists.</li>
                                <li>5. Revisit SWOT every 2-4 weeks and update entries based on measurable progress.</li>
                            </ul>
                        </div>
                        <div className="px-5 py-4 border-t border-gray-100 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setIsSwotTipsModalOpen(false)}
                                className="px-4 py-2 rounded-lg text-xs font-bold bg-gray-900 text-white hover:bg-black transition"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            <div className="mb-6 sm:mb-8 bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="p-4 border-b border-gray-100">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h2 className="text-lg font-extrabold text-gray-900">Season Priorities</h2>
                            <p className="text-sm text-gray-500 mt-1">Choose up to 3 skill priorities and up to 3 athletic priorities.</p>
                        </div>
                        <div className="inline-flex items-center rounded-xl border border-gray-200 bg-gray-50 p-1">
                            <button
                                type="button"
                                onClick={() => setPriorityBucketTab("skill")}
                                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition ${priorityBucketTab === "skill" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
                            >
                                Skill ({groupedPriorityItems.skill.length}/3)
                            </button>
                            <button
                                type="button"
                                onClick={() => setPriorityBucketTab("athletic")}
                                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition ${priorityBucketTab === "athletic" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
                            >
                                Athletic ({groupedPriorityItems.athletic.length}/3)
                            </button>
                        </div>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Click a priority card to expand details.</p>
                    {priorityError ? <p className="mt-2 text-xs font-semibold text-red-600">{priorityError}</p> : null}
                </div>
                <div className="p-4">
                    {visiblePriorityItems.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">
                            {priorityItems.length === 0
                                ? "Star items below to add them here."
                                : `No ${priorityBucketTab === "athletic" ? "athletic" : "skill"} priorities selected yet.`}
                        </p>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePriorityDragEnd}>
                            <SortableContext items={visiblePriorityItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                                <ul className="space-y-3">
                                    {visiblePriorityItems.map((item, idx) => (
                                        <ExpandablePriorityItem
                                            key={item.id}
                                            data={item}
                                            priorityIndex={idx + 1}
                                            sections={sections}
                                            itemsBySection={itemsBySection}
                                            microPointsBySection={microPointsBySection}
                                            expanded={expandedPriorityIds.includes(item.id)}
                                            onToggleExpand={handleTogglePriorityExpanded}
                                            onTogglePriority={handleTogglePriority}
                                            onMicroPointChange={handlePriorityMicroPointChange}
                                            onUpdateChecklistTaskText={handleUpdateChecklistTaskText}
                                            onAddChecklistTask={handleAddChecklistTask}
                                            onRemoveChecklistTask={handleRemoveChecklistTask}
                                            canEdit={canEdit}
                                        />
                                    ))}
                                </ul>
                            </SortableContext>
                        </DndContext>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
                {Object.keys(swot).map((category) => (
                    <div key={category} className="flex flex-col bg-gray-50 border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full">
                        <div className={`p-4 border-b border-gray-100 flex items-center justify-between ${category === 'strengths' ? 'bg-green-100/50' : category === 'weaknesses' ? 'bg-red-100/50' : category === 'opportunities' ? 'bg-blue-100/50' : 'bg-yellow-100/50'}`}>
                            <div>
                                <div className="flex items-center">
                                    <h3 className="text-base sm:text-lg font-extrabold capitalize text-gray-800">{category}</h3>
                                    <InfoTooltip text={swotDefinitions[category]} />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{swotHeaderSubtext[category]}</p>
                            </div>
                            <span className="text-xs font-bold text-gray-500 bg-white px-2 py-1 rounded">{swot[category].length}</span>
                        </div>
                        <div className="flex-grow p-3 sm:p-4 min-h-[180px] sm:min-h-[200px]">
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={e => handleDragEnd(category, e)}>
                                <SortableContext items={swot[category].map(i => i.id)} strategy={verticalListSortingStrategy}>
                                    <ul>{swot[category].map(obj => (<SortableItem key={obj.id} data={{ ...obj, category }} onRemove={id => handleRemoveItem(category, id)} onTogglePriority={handleTogglePriority} onClick={data => canEdit ? setActiveItem({ ...data, category }) : null} canEdit={canEdit} />))}</ul>
                                </SortableContext>
                            </DndContext>
                            {swot[category].length === 0 && <p className="text-center text-sm text-gray-400 mt-8 italic">No items yet</p>}
                        </div>
                        <div className="p-4 sm:p-5 bg-white border-t border-gray-200">
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Add New {category}</h4>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">1. Category</label>
                                    <select className="w-full text-sm border-gray-300 rounded-md border p-2 bg-gray-50 disabled:bg-gray-50 disabled:text-gray-400" value={forms[category].section} onChange={e => updateForm(category, 'section', e.target.value)} disabled={!canEdit}>
                                        <option value="">Select...</option>
                                        {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">2. Item</label>
                                    <select className="w-full text-sm border-gray-300 rounded-md border p-2 bg-gray-50 disabled:opacity-50" value={forms[category].item} onChange={e => updateForm(category, 'item', e.target.value)} disabled={!forms[category].section || !canEdit}>
                                        <option value="">Select Item...</option>
                                        {forms[category].section && itemsBySection[forms[category].section]?.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                                    </select>
                                </div>
                                <button onClick={() => handleAddItem(category)} disabled={!forms[category].item || !canEdit} className="w-full mt-2 py-2 bg-gray-900 hover:bg-black text-white text-sm font-bold rounded-md disabled:opacity-50">+ Add Item</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
